import { useState, useEffect, useRef, useCallback, createContext, useContext, useMemo } from "react";
import "@/App.css";
import axios from "axios";
import { 
  Bell, History, Wifi, WifiOff, Volume2, Copy, Check, Trash2, X, 
  Zap, Smartphone, LogOut, User, Users, Settings, Send, Crown,
  Mail, Lock, Eye, EyeOff, QrCode, Clock, Shield,
  ChevronDown, ChevronUp, Layers
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { ScrollArea } from "@/components/ui/scroll-area";
import { Toaster, toast } from "sonner";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;
const WS_URL = BACKEND_URL.replace('https://', 'wss://').replace('http://', 'ws://') + '/api/ws';

// Named constants (replaces magic numbers)
const HTTP_UNAUTHORIZED = 401;
const POLLING_INTERVAL_MS = 30000;
const WS_PING_INTERVAL_MS = 25000;
const WS_MAX_RECONNECT_DELAY_MS = 30000;
const EXTEND_DAYS_DEFAULT = 30;

const getStrategyTypeClass = (type) => {
  switch (type) {
    case 'reversal': return 'bg-amber-500/20 text-amber-400';
    case 'exit': return 'bg-purple-500/20 text-purple-400';
    case 'message': return 'bg-cyan-500/20 text-cyan-400';
    case 'avg': return 'bg-orange-500/20 text-orange-400';
    case 'news': return 'bg-blue-500/20 text-blue-400';
    case 'pair': return 'bg-fuchsia-500/20 text-fuchsia-400';
    default: return 'bg-[#71717a]/20 text-[#71717a]';
  }
};

// Parse "RELIANCE (Avg 2)" → { base: "RELIANCE", avgN: 2 }. Returns null if no match.
const parseAvgSymbol = (symbol) => {
  if (!symbol) return null;
  const m = symbol.match(/^(.+?)\s*\(Avg\s+(\d+)\)\s*$/i);
  return m ? { base: m[1].trim(), avgN: parseInt(m[2], 10) } : null;
};

// Parse "RELIANCE (Exit)" → "RELIANCE"
const parseExitSymbol = (symbol) => {
  if (!symbol) return null;
  const m = symbol.match(/^(.+?)\s*\(Exit\)\s*$/i);
  return m ? m[1].trim() : null;
};

// Auth Context
const AuthContext = createContext(null);

const useAuth = () => useContext(AuthContext);

// Auth Provider
const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [token, setToken] = useState(localStorage.getItem('token'));
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (token) {
      fetchUser();
    } else {
      setLoading(false);
    }
  }, [token]);

  const fetchUser = async () => {
    try {
      const res = await axios.get(`${API}/auth/me`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setUser(res.data);
    } catch (e) {
      // Check if session expired (logged in from another device)
      if (e.response?.status === HTTP_UNAUTHORIZED) {
        const message = e.response?.data?.detail || '';
        if (message.includes('another device')) {
          toast.error('You have been logged out because you logged in from another device.');
        }
      }
      localStorage.removeItem('token');
      setToken(null);
      setUser(null);
    }
    setLoading(false);
  };

  const login = async (mobile, password, email = '') => {
    const res = await axios.post(`${API}/auth/login`, { mobile, password, email });
    localStorage.setItem('token', res.data.token);
    setToken(res.data.token);
    setUser(res.data.user);
    return res.data;
  };

  const register = async (mobile, password, name) => {
    const res = await axios.post(`${API}/auth/register`, { mobile, password, name });
    localStorage.setItem('token', res.data.token);
    setToken(res.data.token);
    setUser(res.data.user);
    return res.data;
  };

  const logout = () => {
    localStorage.removeItem('token');
    setToken(null);
    setUser(null);
  };

  const refreshUser = async () => {
    if (token) await fetchUser();
  };

  const authValue = useMemo(() => ({ user, token, loading, login, register, logout, refreshUser }), [user, token, loading]);

  return (
    <AuthContext.Provider value={authValue}>
      {children}
    </AuthContext.Provider>
  );
};

// Login Page
const LoginPage = ({ onSwitchToRegister }) => {
  const { login } = useAuth();
  const [mobile, setMobile] = useState('');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const [isAdminLogin, setIsAdminLogin] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (isAdminLogin) {
      // Admin login with email
      if (!email.includes('@')) {
        toast.error('Please enter a valid email');
        return;
      }
    } else {
      // User login with mobile
      if (mobile.length !== 10 || !/^\d+$/.test(mobile)) {
        toast.error('Please enter a valid 10-digit mobile number');
        return;
      }
    }
    
    setLoading(true);
    try {
      await login(isAdminLogin ? '' : mobile, password, isAdminLogin ? email : '');
      toast.success('Login successful!');
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Login failed');
    }
    setLoading(false);
  };

  return (
    <div className="auth-page" data-testid="login-page">
      <div className="auth-card">
        <img src="/logo.png" alt="Wolffs InstaAlerts" className="w-20 h-20 mx-auto mb-4 logo-blend" />
        <h1 className="text-2xl font-bold text-[#d4a84b] mb-2 font-mono">WOLFFS INSTAALERTS</h1>
        <p className="text-[#71717a] text-sm mb-8">Sign in to your account</p>
        
        <form onSubmit={handleSubmit} className="space-y-4">
          {isAdminLogin ? (
            <div className="relative">
              <Mail className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-[#71717a]" />
              <Input
                type="email"
                placeholder="Admin Email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                className="pl-10 bg-[#0f1f35] border-[#d4a84b]/30 text-white"
                required
                data-testid="login-email"
              />
            </div>
          ) : (
            <div className="relative">
              <Smartphone className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-[#71717a]" />
              <Input
                type="tel"
                placeholder="Mobile Number"
                value={mobile}
                onChange={(e) => setMobile(e.target.value.replace(/\D/g, '').slice(0, 10))}
                className="pl-10 bg-[#0f1f35] border-[#d4a84b]/30 text-white"
                required
                maxLength={10}
                data-testid="login-mobile"
              />
            </div>
          )}
          
          <div className="relative">
            <Lock className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-[#71717a]" />
            <Input
              type={showPassword ? "text" : "password"}
              placeholder="Password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              className="pl-10 pr-10 bg-[#0f1f35] border-[#d4a84b]/30 text-white"
              required
              data-testid="login-password"
            />
            <button
              type="button"
              onClick={() => setShowPassword(!showPassword)}
              className="absolute right-3 top-1/2 -translate-y-1/2 text-[#71717a] hover:text-white"
            >
              {showPassword ? <EyeOff className="w-5 h-5" /> : <Eye className="w-5 h-5" />}
            </button>
          </div>
          
          <Button
            type="submit"
            className="w-full bg-[#d4a84b] text-[#0a1628] hover:bg-[#c49a40] font-semibold"
            disabled={loading}
            data-testid="login-submit"
          >
            {loading ? 'Signing in...' : 'Sign In'}
          </Button>
        </form>
        
        <p className="text-center text-[#71717a] text-sm mt-6">
          Don't have an account?{' '}
          <button onClick={onSwitchToRegister} className="text-[#d4a84b] hover:underline" data-testid="switch-to-register">
            Register
          </button>
        </p>
        
        <button 
          onClick={() => setIsAdminLogin(!isAdminLogin)} 
          className="text-center text-[#71717a] text-xs mt-4 block mx-auto hover:text-[#d4a84b]"
        >
          {isAdminLogin ? '← Back to User Login' : 'Admin Login'}
        </button>
      </div>
    </div>
  );
};

// Register Page — 2-step: collect details → OTP verification → register
const RegisterPage = ({ onSwitchToLogin }) => {
  const { register } = useAuth();
  const [step, setStep] = useState('details'); // 'details' | 'otp'
  const [mobile, setMobile] = useState('');
  const [name, setName] = useState('');
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  // OTP state
  const [otp, setOtp] = useState('');
  const [otpCountdown, setOtpCountdown] = useState(0); // seconds until resend allowed
  const [otpExpiresIn, setOtpExpiresIn] = useState(0); // seconds until OTP expiry

  // Simple 6-digit password validation
  const passwordValid = password.length === 6 && /^\d+$/.test(password);
  const mobileValid = mobile.length === 10 && /^\d+$/.test(mobile);
  const nameValid = name.trim().length >= 2;
  const otpValid = otp.length === 6 && /^\d+$/.test(otp);
  const allValid = passwordValid && password === confirmPassword && mobileValid && nameValid;

  // Countdown timers
  useEffect(() => {
    if (otpCountdown <= 0) return;
    const t = setInterval(() => setOtpCountdown((s) => Math.max(0, s - 1)), 1000);
    return () => clearInterval(t);
  }, [otpCountdown]);

  useEffect(() => {
    if (otpExpiresIn <= 0) return;
    const t = setInterval(() => setOtpExpiresIn((s) => Math.max(0, s - 1)), 1000);
    return () => clearInterval(t);
  }, [otpExpiresIn]);

  const formatTime = (s) => `${Math.floor(s / 60)}:${(s % 60).toString().padStart(2, '0')}`;

  const handleSendOtp = async (e) => {
    e.preventDefault();
    if (!allValid) {
      toast.error('Please fill in all fields correctly');
      return;
    }
    setLoading(true);
    try {
      const res = await axios.post(`${API}/otp/send`, { mobile });
      toast.success(`OTP sent to +91 ${mobile}`);
      setOtpExpiresIn(res.data.expires_in || 600);
      setOtpCountdown(60); // 60s before resend allowed
      setStep('otp');
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to send OTP');
    }
    setLoading(false);
  };

  const handleResendOtp = async () => {
    if (otpCountdown > 0) return;
    setLoading(true);
    try {
      const res = await axios.post(`${API}/otp/resend`, { mobile });
      toast.success('OTP resent');
      setOtpExpiresIn(res.data.expires_in || 600);
      setOtpCountdown(60);
      setOtp('');
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to resend OTP');
    }
    setLoading(false);
  };

  const handleVerifyAndRegister = async (e) => {
    e.preventDefault();
    if (!otpValid) {
      toast.error('Enter the 6-digit OTP');
      return;
    }
    setLoading(true);
    try {
      // Step 1: verify OTP
      await axios.post(`${API}/otp/verify`, { mobile, otp });
      // Step 2: create the account
      await register(mobile, password, name);
      toast.success('Account created successfully!');
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Verification failed');
    }
    setLoading(false);
  };

  return (
    <div className="auth-page" data-testid="register-page">
      <div className="auth-card">
        <img src="/logo.png" alt="Wolffs InstaAlerts" className="w-20 h-20 mx-auto mb-4 logo-blend" />
        <h1 className="text-2xl font-bold text-[#d4a84b] mb-2 font-mono">
          {step === 'details' ? 'CREATE ACCOUNT' : 'VERIFY MOBILE'}
        </h1>
        <p className="text-[#71717a] text-sm mb-6">
          {step === 'details' ? 'Join Wolffs InstaAlerts' : `Enter the 6-digit OTP sent to +91 ${mobile}`}
        </p>

        {step === 'details' ? (
          <form onSubmit={handleSendOtp} className="space-y-4">
            <div className="relative">
              <Smartphone className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-[#71717a]" />
              <div className="absolute left-10 top-1/2 -translate-y-1/2 text-[#71717a] text-sm pointer-events-none">+91</div>
              <Input
                type="tel"
                placeholder="Mobile Number (10 digits)"
                value={mobile}
                onChange={(e) => setMobile(e.target.value.replace(/\D/g, '').slice(0, 10))}
                className="pl-16 bg-[#0f1f35] border-[#d4a84b]/30 text-white"
                required
                maxLength={10}
                data-testid="register-mobile"
              />
            </div>

            <div className="relative">
              <User className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-[#71717a]" />
              <Input
                type="text"
                placeholder="Your Name"
                value={name}
                onChange={(e) => setName(e.target.value)}
                className="pl-10 bg-[#0f1f35] border-[#d4a84b]/30 text-white"
                required
                data-testid="register-name"
              />
            </div>

            <div className="relative">
              <Lock className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-[#71717a]" />
              <Input
                type={showPassword ? "text" : "password"}
                placeholder="6-digit Password"
                value={password}
                onChange={(e) => setPassword(e.target.value.replace(/\D/g, '').slice(0, 6))}
                className="pl-10 pr-10 bg-[#0f1f35] border-[#d4a84b]/30 text-white"
                required
                maxLength={6}
                data-testid="register-password"
              />
              <button
                type="button"
                onClick={() => setShowPassword(!showPassword)}
                className="absolute right-3 top-1/2 -translate-y-1/2 text-[#71717a] hover:text-white"
              >
                {showPassword ? <EyeOff className="w-5 h-5" /> : <Eye className="w-5 h-5" />}
              </button>
            </div>

            <div className="relative">
              <Lock className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-[#71717a]" />
              <Input
                type={showPassword ? "text" : "password"}
                placeholder="Confirm 6-digit Password"
                value={confirmPassword}
                onChange={(e) => setConfirmPassword(e.target.value.replace(/\D/g, '').slice(0, 6))}
                className="pl-10 bg-[#0f1f35] border-[#d4a84b]/30 text-white"
                required
                maxLength={6}
                data-testid="register-confirm-password"
              />
            </div>

            <div className="bg-[#0f1f35] rounded-lg p-3 space-y-1">
              <div className={`text-xs flex items-center gap-2 ${mobileValid ? 'text-green-400' : 'text-[#71717a]'}`}>
                {mobileValid ? <Check className="w-3 h-3" /> : <X className="w-3 h-3" />}
                Valid 10-digit mobile number
              </div>
              <div className={`text-xs flex items-center gap-2 ${nameValid ? 'text-green-400' : 'text-[#71717a]'}`}>
                {nameValid ? <Check className="w-3 h-3" /> : <X className="w-3 h-3" />}
                Name entered
              </div>
              <div className={`text-xs flex items-center gap-2 ${passwordValid ? 'text-green-400' : 'text-[#71717a]'}`}>
                {passwordValid ? <Check className="w-3 h-3" /> : <X className="w-3 h-3" />}
                6-digit numeric password
              </div>
              <div className={`text-xs flex items-center gap-2 ${password === confirmPassword && password ? 'text-green-400' : 'text-[#71717a]'}`}>
                {password === confirmPassword && password ? <Check className="w-3 h-3" /> : <X className="w-3 h-3" />}
                Passwords match
              </div>
            </div>

            <Button
              type="submit"
              className="w-full bg-[#d4a84b] text-[#0a1628] hover:bg-[#c49a40] font-semibold"
              disabled={loading || !allValid}
              data-testid="register-send-otp"
            >
              {loading ? 'Sending OTP...' : 'Send OTP'}
            </Button>
          </form>
        ) : (
          <form onSubmit={handleVerifyAndRegister} className="space-y-4">
            <div className="relative">
              <Lock className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-[#71717a]" />
              <Input
                type="tel"
                placeholder="6-digit OTP"
                value={otp}
                onChange={(e) => setOtp(e.target.value.replace(/\D/g, '').slice(0, 6))}
                className="pl-10 bg-[#0f1f35] border-[#d4a84b]/30 text-white text-center text-lg tracking-[0.5em] font-mono"
                required
                maxLength={6}
                autoFocus
                data-testid="register-otp"
              />
            </div>

            <div className="flex items-center justify-between text-xs">
              <span className="text-[#71717a]">
                {otpExpiresIn > 0 ? `OTP expires in ${formatTime(otpExpiresIn)}` : <span className="text-red-400">OTP expired</span>}
              </span>
              <button
                type="button"
                onClick={handleResendOtp}
                disabled={otpCountdown > 0 || loading}
                className="text-[#d4a84b] hover:underline disabled:text-[#71717a] disabled:no-underline disabled:cursor-not-allowed"
                data-testid="register-resend-otp"
              >
                {otpCountdown > 0 ? `Resend in ${otpCountdown}s` : 'Resend OTP'}
              </button>
            </div>

            <Button
              type="submit"
              className="w-full bg-[#d4a84b] text-[#0a1628] hover:bg-[#c49a40] font-semibold"
              disabled={loading || !otpValid}
              data-testid="register-verify-submit"
            >
              {loading ? 'Verifying...' : 'Verify & Create Account'}
            </Button>

            <button
              type="button"
              onClick={() => { setStep('details'); setOtp(''); }}
              className="w-full text-center text-[#71717a] text-sm hover:text-white"
              data-testid="register-back-to-details"
            >
              ← Change details
            </button>
          </form>
        )}

        <p className="text-center text-[#71717a] text-sm mt-6">
          Already have an account?{' '}
          <button onClick={onSwitchToLogin} className="text-[#d4a84b] hover:underline" data-testid="switch-to-login">
            Sign In
          </button>
        </p>
      </div>
    </div>
  );
};

// Plan Selection Page
const PlanSelectionPage = () => {
  const { user, token, refreshUser } = useAuth();
  const [paymentInfo, setPaymentInfo] = useState(null);
  const [showPayment, setShowPayment] = useState(false);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    fetchPaymentInfo();
  }, []);

  const fetchPaymentInfo = async () => {
    try {
      const res = await axios.get(`${API}/plans/payment-info`);
      setPaymentInfo(res.data);
    } catch (e) {
      console.error('Failed to fetch payment info:', e);
    }
  };

  const selectPlan = async (planType) => {
    setLoading(true);
    try {
      await axios.post(`${API}/plans/select`, { plan_type: planType }, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      if (planType === 'trial') {
        toast.success('2-day free trial activated!');
        await refreshUser();
      } else {
        setShowPayment(true);
        toast.info('Please complete payment and wait for admin verification');
        await refreshUser();
      }
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to select plan');
    }
    setLoading(false);
  };

  if (user?.membership_status === 'pending_payment' || showPayment) {
    return (
      <div className="auth-page" data-testid="payment-page">
        <div className="auth-card max-w-lg">
          <QrCode className="w-12 h-12 text-[#d4a84b] mx-auto mb-4" />
          <h1 className="text-xl font-bold text-white mb-2">Complete Payment</h1>
          <p className="text-[#71717a] text-sm mb-6">Pay using the details below and wait for admin verification</p>
          
          {paymentInfo?.qr_code_base64 && (
            <div className="mb-6">
              <img 
                src={paymentInfo.qr_code_base64} 
                alt="Payment QR Code" 
                className="w-48 h-48 mx-auto rounded-lg border border-[#d4a84b]/30"
              />
            </div>
          )}
          
          {paymentInfo?.upi_id && (
            <div className="bg-[#0f1f35] rounded-lg p-4 mb-4">
              <p className="text-xs text-[#71717a] mb-1">UPI ID</p>
              <p className="text-[#d4a84b] font-mono">{paymentInfo.upi_id}</p>
            </div>
          )}
          
          {paymentInfo?.bank_name && (
            <div className="bg-[#0f1f35] rounded-lg p-4 space-y-2 text-left">
              <p className="text-xs text-[#71717a]">Bank Details</p>
              <p className="text-white text-sm">Bank: {paymentInfo.bank_name}</p>
              <p className="text-white text-sm">A/C: {paymentInfo.account_number}</p>
              <p className="text-white text-sm">IFSC: {paymentInfo.ifsc_code}</p>
              <p className="text-white text-sm">Name: {paymentInfo.account_holder}</p>
            </div>
          )}
          
          {!paymentInfo?.qr_code_base64 && !paymentInfo?.bank_name && !paymentInfo?.upi_id && (
            <p className="text-[#71717a]">Payment details not configured. Please contact admin.</p>
          )}
          
          <div className="mt-6 p-4 bg-[#d4a84b]/10 rounded-lg border border-[#d4a84b]/30">
            <p className="text-[#d4a84b] text-sm">
              <Clock className="w-4 h-4 inline mr-2" />
              Your account is pending verification. You'll receive access once admin confirms your payment.
            </p>
          </div>
        </div>
      </div>
    );
  }

  // Show only paid option if trial expired
  const showTrialOption = user?.membership_status !== 'expired' && user?.plan_type !== 'trial';

  return (
    <div className="auth-page" data-testid="plan-selection-page">
      <div className="auth-card max-w-xl">
        <Crown className="w-12 h-12 text-[#d4a84b] mx-auto mb-4" />
        <h1 className="text-xl font-bold text-white mb-2">Choose Your Plan</h1>
        <p className="text-[#71717a] text-sm mb-8">Select a plan to access stock alerts</p>
        
        <div className="grid gap-4">
          {showTrialOption && (
            <div className="plan-card" data-testid="trial-plan">
              <div className="flex justify-between items-start mb-4">
                <div>
                  <h3 className="text-lg font-semibold text-white">Free Trial</h3>
                  <p className="text-[#71717a] text-sm">Try before you buy</p>
                </div>
                <span className="text-2xl font-bold text-[#d4a84b]">2 Days</span>
              </div>
              <ul className="space-y-2 mb-4 text-sm text-[#a1a1aa]">
                <li className="flex items-center gap-2"><Check className="w-4 h-4 text-green-400" /> All stock alerts</li>
                <li className="flex items-center gap-2"><Check className="w-4 h-4 text-green-400" /> Real-time notifications</li>
                <li className="flex items-center gap-2"><Check className="w-4 h-4 text-green-400" /> No credit card required</li>
              </ul>
              <Button
                onClick={() => selectPlan('trial')}
                className="w-full bg-transparent border border-[#d4a84b] text-[#d4a84b] hover:bg-[#d4a84b] hover:text-[#0a1628]"
                disabled={loading}
                data-testid="select-trial"
              >
                Start Free Trial
              </Button>
            </div>
          )}
          
          <div className="plan-card border-[#d4a84b]" data-testid="paid-plan">
            <div className="absolute -top-3 left-1/2 -translate-x-1/2 bg-[#d4a84b] text-[#0a1628] px-3 py-1 rounded-full text-xs font-semibold">
              RECOMMENDED
            </div>
            <div className="flex justify-between items-start mb-4">
              <div>
                <h3 className="text-lg font-semibold text-white">Premium</h3>
                <p className="text-[#71717a] text-sm">Full access</p>
              </div>
              <span className="text-2xl font-bold text-[#d4a84b]">30 Days</span>
            </div>
            <ul className="space-y-2 mb-4 text-sm text-[#a1a1aa]">
              <li className="flex items-center gap-2"><Check className="w-4 h-4 text-green-400" /> All stock alerts</li>
              <li className="flex items-center gap-2"><Check className="w-4 h-4 text-green-400" /> Real-time notifications</li>
              <li className="flex items-center gap-2"><Check className="w-4 h-4 text-green-400" /> Priority support</li>
              <li className="flex items-center gap-2"><Check className="w-4 h-4 text-green-400" /> Admin messages</li>
            </ul>
            <Button
              onClick={() => selectPlan('paid')}
              className="w-full bg-[#d4a84b] text-[#0a1628] hover:bg-[#c49a40]"
              disabled={loading}
              data-testid="select-paid"
            >
              Get Premium
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
};

// Strategy Selection Page
const StrategySelectionPage = ({ onDone }) => {
  const { user, token, refreshUser } = useAuth();
  const [strategies, setStrategies] = useState([]);
  const [selected, setSelected] = useState(new Set(user?.selected_strategies || []));
  const [expanded, setExpanded] = useState({});
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    fetchStrategies();
  }, []);

  const fetchStrategies = async () => {
    try {
      const res = await axios.get(`${API}/strategies`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      const list = res.data.strategies || [];
      setStrategies(list);
      // Drop any previously-selected strategies that are now locked (trial restriction changed)
      setSelected(prev => {
        const accessibleIds = new Set(list.filter(s => s.accessible !== false).map(s => s.id));
        const next = new Set();
        prev.forEach(id => { if (accessibleIds.has(id)) next.add(id); });
        return next;
      });
    } catch (e) {
      toast.error('Failed to load strategies');
    }
    setLoading(false);
  };

  const toggleStrategy = (id) => {
    const strat = strategies.find(s => s.id === id);
    // Allow deselecting always; only block ADDING a locked strategy
    setSelected(prev => {
      const next = new Set(prev);
      if (next.has(id)) {
        next.delete(id);
        return next;
      }
      if (strat && strat.accessible === false) {
        toast.error('Subscribe for full access to this strategy');
        return prev;
      }
      next.add(id);
      return next;
    });
  };

  const toggleExpand = (id) => {
    setExpanded(prev => ({ ...prev, [id]: !prev[id] }));
  };

  const handleContinue = async () => {
    if (selected.size === 0) {
      toast.error('Please select at least one strategy');
      return;
    }
    setSaving(true);
    try {
      await axios.put(`${API}/user/strategies`, 
        { strategy_ids: Array.from(selected) },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      await refreshUser();
      if (onDone) onDone();
      toast.success('Strategies saved!');
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to save strategies');
    }
    setSaving(false);
  };

  const strategyColors = [
    { border: 'border-emerald-500/40', bg: 'bg-emerald-500/10', text: 'text-emerald-400', check: 'bg-emerald-500' },
    { border: 'border-blue-500/40', bg: 'bg-blue-500/10', text: 'text-blue-400', check: 'bg-blue-500' },
    { border: 'border-amber-500/40', bg: 'bg-amber-500/10', text: 'text-amber-400', check: 'bg-amber-500' },
    { border: 'border-purple-500/40', bg: 'bg-purple-500/10', text: 'text-purple-400', check: 'bg-purple-500' },
  ];

  if (loading) {
    return (
      <div className="auth-page">
        <div className="text-center">
          <Layers className="w-12 h-12 text-[#d4a84b] mx-auto mb-4 animate-pulse" />
          <p className="text-[#71717a]">Loading strategies...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="auth-page" data-testid="strategy-selection-page">
      <Toaster position="top-right" theme="dark" />
      <div className="w-full max-w-lg mx-auto px-4 py-8">
        <div className="text-center mb-8">
          <Layers className="w-12 h-12 text-[#d4a84b] mx-auto mb-3" />
          <h1 className="text-xl font-bold text-white mb-1 font-mono">SELECT STRATEGIES</h1>
          <p className="text-[#71717a] text-sm">Choose the strategies you want to receive alerts for</p>
        </div>

        <div className="space-y-3 mb-8">
          {strategies.map((strat, idx) => {
            const colors = strategyColors[idx % strategyColors.length];
            const isSelected = selected.has(strat.id);
            const isExpanded = expanded[strat.id];
            const isLocked = strat.accessible === false;
            
            return (
              <div
                key={strat.id}
                className={`rounded-xl border-2 transition-all duration-200 overflow-hidden ${
                  isLocked ? 'border-[#1e293b] bg-[#0f1f35]/30 opacity-60' :
                  isSelected ? `${colors.border} ${colors.bg}` : 'border-[#1e293b] bg-[#0f1f35]/60'
                }`}
                data-testid={`strategy-card-${idx}`}
              >
                <div className="flex items-center gap-3 p-4">
                  {/* Checkbox */}
                  <button
                    onClick={() => toggleStrategy(strat.id)}
                    className={`w-6 h-6 rounded-md border-2 flex items-center justify-center shrink-0 transition-all ${
                      isLocked ? 'border-[#404040] bg-transparent cursor-not-allowed' :
                      isSelected ? `${colors.check} border-transparent` : 'border-[#404040] bg-transparent'
                    }`}
                    data-testid={`strategy-checkbox-${idx}`}
                  >
                    {isLocked ? <Lock className="w-3.5 h-3.5 text-[#71717a]" /> :
                     isSelected && <Check className="w-4 h-4 text-white" />}
                  </button>

                  {/* Name + expand trigger */}
                  <button
                    onClick={() => toggleExpand(strat.id)}
                    className="flex-1 flex items-center justify-between text-left"
                  >
                    <div className="flex items-center gap-2">
                      <span className={`font-semibold text-base ${isLocked ? 'text-[#71717a]' : isSelected ? colors.text : 'text-white/80'}`}>
                        {strat.name}
                      </span>
                      {isLocked && (
                        <span className="text-[10px] px-2 py-0.5 rounded-full bg-[#d4a84b]/20 text-[#d4a84b]">PRO</span>
                      )}
                    </div>
                    {isExpanded
                      ? <ChevronUp className="w-5 h-5 text-[#71717a]" />
                      : <ChevronDown className="w-5 h-5 text-[#71717a]" />
                    }
                  </button>
                </div>

                {/* Expandable description */}
                {isExpanded && (
                  <div className="px-4 pb-4 pt-0">
                    <div className="ml-9 text-sm text-[#a1a1aa] leading-relaxed border-t border-white/5 pt-3">
                      {strat.description || 'No description available.'}
                    </div>
                    {isLocked && (
                      <div className="ml-9 mt-2 text-xs text-[#d4a84b]">
                        Subscribe to access this strategy
                      </div>
                    )}
                    <div className="ml-9 mt-2">
                      <span className={`text-xs px-2 py-0.5 rounded-full ${getStrategyTypeClass(strat.strategy_type)}`}>
                        {strat.strategy_type.toUpperCase()}
                      </span>
                    </div>
                  </div>
                )}
              </div>
            );
          })}
        </div>

        <Button
          onClick={handleContinue}
          className="w-full bg-[#d4a84b] text-[#0a1628] hover:bg-[#c49a40] font-semibold h-12 text-base"
          disabled={saving || selected.size === 0}
          data-testid="strategy-continue-btn"
        >
          {saving ? 'Saving...' : `Continue to Alerts (${selected.size} selected)`}
        </Button>

        <p className="text-center text-[#71717a] text-xs mt-4">
          You can change your selection anytime from the settings menu
        </p>
      </div>
    </div>
  );
};

// Admin Dashboard
const AdminDashboard = () => {
  const { user, token, logout } = useAuth();
  const [users, setUsers] = useState([]);
  const [stats, setStats] = useState({});
  const [paymentSettings, setPaymentSettings] = useState({});
  const [adminMessage, setAdminMessage] = useState('');
  const [activeTab, setActiveTab] = useState('users');
  const [loading, setLoading] = useState(false);
  const [razorpayAccounts, setRazorpayAccounts] = useState([]);
  const [newRazorpayAccount, setNewRazorpayAccount] = useState({ name: '', key_id: '', key_secret: '' });
  const [welcomeMessage, setWelcomeMessage] = useState('');
  const [privateMessage, setPrivateMessage] = useState('');
  const [selectedUserId, setSelectedUserId] = useState('');
  const [testBuyStock, setTestBuyStock] = useState('');
  const [testSellStock, setTestSellStock] = useState('');
  const [adminStrategies, setAdminStrategies] = useState([]);
  const [editingStrategy, setEditingStrategy] = useState({});
  const [stratMsgBuy, setStratMsgBuy] = useState({});
  const [stratMsgSell, setStratMsgSell] = useState({});
  const [adminTrialIds, setAdminTrialIds] = useState([]);
  const [adminPaidIds, setAdminPaidIds] = useState([]);
  const [stratBroadcast, setStratBroadcast] = useState({});
  const [serverIp, setServerIp] = useState(null);
  const [loadingIp, setLoadingIp] = useState(false);
  const [diagMobile, setDiagMobile] = useState('');
  const [diagResult, setDiagResult] = useState(null);
  const [diagLoading, setDiagLoading] = useState(false);

  const fetchServerIp = async () => {
    setLoadingIp(true);
    try {
      const res = await axios.get(`${API}/admin/server-ip`, { headers: { Authorization: `Bearer ${token}` } });
      setServerIp(res.data.outbound_ips);
    } catch (e) {
      toast.error('Failed to fetch server IP');
    }
    setLoadingIp(false);
  };

  const runMsg91Diagnose = async () => {
    if (!/^\d{10}$/.test(diagMobile)) {
      toast.error('Enter a valid 10-digit mobile');
      return;
    }
    setDiagLoading(true);
    setDiagResult(null);
    try {
      const res = await axios.post(`${API}/admin/msg91-diagnose`, { mobile: diagMobile }, { headers: { Authorization: `Bearer ${token}` } });
      setDiagResult(res.data);
      toast.success('Diagnostic sent — check phone in ~30s');
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Diagnostic failed');
    }
    setDiagLoading(false);
  };

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      const [usersRes, statsRes, paymentRes, razorpayRes, welcomeRes, strategiesRes] = await Promise.all([
        axios.get(`${API}/admin/users`, { headers: { Authorization: `Bearer ${token}` } }),
        axios.get(`${API}/admin/stats`, { headers: { Authorization: `Bearer ${token}` } }),
        axios.get(`${API}/plans/payment-info`),
        axios.get(`${API}/admin/razorpay-accounts`, { headers: { Authorization: `Bearer ${token}` } }),
        axios.get(`${API}/admin/welcome-message`, { headers: { Authorization: `Bearer ${token}` } }),
        axios.get(`${API}/admin/strategies`, { headers: { Authorization: `Bearer ${token}` } })
      ]);
      setUsers(usersRes.data.users);
      setStats(statsRes.data);
      setPaymentSettings(paymentRes.data);
      setRazorpayAccounts(razorpayRes.data.accounts || []);
      setWelcomeMessage(welcomeRes.data.welcome_message || '');
      setAdminStrategies(strategiesRes.data.strategies || []);
      setAdminTrialIds(strategiesRes.data.trial_strategy_ids || []);
      setAdminPaidIds(strategiesRes.data.paid_strategy_ids || []);
    } catch (e) {
      console.error('Failed to load admin data:', e);
    }
  };

  const verifyUser = async (userId) => {
    try {
      await axios.patch(`${API}/admin/users/${userId}/verify`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('User verified and membership activated');
      fetchData();
    } catch (e) {
      toast.error('Failed to verify user');
    }
  };

  const extendMembership = async (userId, days) => {
    try {
      await axios.patch(`${API}/admin/users/${userId}/extend`, { days }, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success(`Membership extended by ${days} days`);
      fetchData();
    } catch (e) {
      toast.error('Failed to extend membership');
    }
  };

  const deleteUser = async (userId) => {
    if (!window.confirm('Are you sure you want to delete this user?')) return;
    try {
      await axios.delete(`${API}/admin/users/${userId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('User deleted');
      fetchData();
    } catch (e) {
      toast.error('Failed to delete user');
    }
  };

  const sendMessage = async () => {
    if (!adminMessage.trim()) return;
    setLoading(true);
    try {
      await axios.post(`${API}/admin/message`, { message: adminMessage }, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Message sent to all users');
      setAdminMessage('');
    } catch (e) {
      toast.error('Failed to send message');
    }
    setLoading(false);
  };

  const updatePaymentSettings = async () => {
    setLoading(true);
    try {
      await axios.put(`${API}/admin/payment-settings`, paymentSettings, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Payment settings updated');
    } catch (e) {
      toast.error('Failed to update settings');
    }
    setLoading(false);
  };

  const handleQRUpload = (e) => {
    const file = e.target.files[0];
    if (file) {
      const reader = new FileReader();
      reader.onloadend = () => {
        setPaymentSettings({ ...paymentSettings, qr_code_base64: reader.result });
      };
      reader.readAsDataURL(file);
    }
  };

  // Razorpay account management
  const addRazorpayAccount = async () => {
    if (!newRazorpayAccount.name || !newRazorpayAccount.key_id || !newRazorpayAccount.key_secret) {
      toast.error('Please fill all fields');
      return;
    }
    setLoading(true);
    try {
      await axios.post(`${API}/admin/razorpay-accounts`, newRazorpayAccount, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Razorpay account added');
      setNewRazorpayAccount({ name: '', key_id: '', key_secret: '' });
      fetchData();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to add account');
    }
    setLoading(false);
  };

  const activateRazorpayAccount = async (accountId) => {
    try {
      await axios.patch(`${API}/admin/razorpay-accounts/${accountId}/activate`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Account activated');
      fetchData();
    } catch (e) {
      toast.error('Failed to activate account');
    }
  };

  const deleteRazorpayAccount = async (accountId) => {
    if (!window.confirm('Delete this Razorpay account?')) return;
    try {
      await axios.delete(`${API}/admin/razorpay-accounts/${accountId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Account deleted');
      fetchData();
    } catch (e) {
      toast.error('Failed to delete account');
    }
  };

  const saveWelcomeMessage = async () => {
    setLoading(true);
    try {
      await axios.post(`${API}/admin/welcome-message`, { message: welcomeMessage }, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Welcome message updated');
    } catch (e) {
      toast.error('Failed to update welcome message');
    }
    setLoading(false);
  };

  const sendPrivateMessage = async () => {
    if (!selectedUserId || !privateMessage.trim()) {
      toast.error('Please select a user and enter a message');
      return;
    }
    setLoading(true);
    try {
      await axios.post(`${API}/admin/send-private-message/${selectedUserId}`, 
        { message: privateMessage },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success('Private message sent');
      setPrivateMessage('');
      setSelectedUserId('');
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to send message');
    }
    setLoading(false);
  };

  const sendTestAlert = async (type) => {
    const stock = type === 'buy' ? testBuyStock : testSellStock;
    if (!stock.trim()) {
      toast.error('Please enter a stock symbol');
      return;
    }
    setLoading(true);
    try {
      await axios.post(`${API}/webhook/chartink/${type}`, {
        stocks: stock.trim(),
        scan_name: `Test ${type.toUpperCase()} Alert`,
        triggered_at: new Date().toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })
      });
      toast.success(`${type.toUpperCase()} alert sent for ${stock}`);
      if (type === 'buy') {
        setTestBuyStock('');
      } else {
        setTestSellStock('');
      }
    } catch (e) {
      toast.error(`Failed to send ${type} alert`);
    }
    setLoading(false);
  };

  const updateStrategy = async (strategyId) => {
    const edit = editingStrategy[strategyId];
    if (!edit) return;
    setLoading(true);
    try {
      await axios.put(`${API}/admin/strategies/${strategyId}`, edit, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Strategy updated');
      setEditingStrategy(prev => { const n = {...prev}; delete n[strategyId]; return n; });
      fetchData();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to update strategy');
    }
    setLoading(false);
  };

  const startEditingStrategy = (strat) => {
    setEditingStrategy(prev => ({
      ...prev,
      [strat.id]: { name: strat.name, description: strat.description, strategy_type: strat.strategy_type }
    }));
  };

  const updateEditField = (stratId, field, value) => {
    setEditingStrategy(prev => ({
      ...prev,
      [stratId]: { ...prev[stratId], [field]: value }
    }));
  };

  const toggleAdminTrialId = (stratId) => {
    setAdminTrialIds(prev => prev.includes(stratId) ? prev.filter(id => id !== stratId) : [...prev, stratId]);
  };

  const saveTrialStrategies = async () => {
    setLoading(true);
    try {
      await axios.put(`${API}/admin/trial-strategies`, { strategy_ids: adminTrialIds }, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Trial strategies saved');
    } catch (e) {
      toast.error('Failed to save trial strategies');
    }
    setLoading(false);
  };

  const toggleAdminPaidId = (stratId) => {
    setAdminPaidIds(prev => prev.includes(stratId) ? prev.filter(id => id !== stratId) : [...prev, stratId]);
  };

  const savePaidStrategies = async () => {
    setLoading(true);
    try {
      await axios.put(`${API}/admin/paid-strategies`, { strategy_ids: adminPaidIds }, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Paid strategies saved');
    } catch (e) {
      toast.error('Failed to save paid strategies');
    }
    setLoading(false);
  };

  const sendStrategyBroadcast = async (strategyId) => {
    const message = (stratBroadcast[strategyId] || '').trim();
    if (!message) {
      toast.error('Enter a message to send');
      return;
    }
    setLoading(true);
    try {
      await axios.post(`${API}/admin/strategy-broadcast/${strategyId}`,
        { message },
        { headers: { Authorization: `Bearer ${token}` } });
      toast.success('Strategy broadcast sent');
      setStratBroadcast(prev => ({ ...prev, [strategyId]: '' }));
    } catch (e) {
      toast.error('Failed to send broadcast');
    }
    setLoading(false);
  };

  const resetStrategyData = async (strategyId, strategyName) => {
    const confirmed = window.confirm(
      `Reset all data for "${strategyName}"?\n\nThis permanently deletes ALL past notifications and active alerts for this strategy. The strategy itself stays. Users will see no data until new alerts come in.\n\nThis cannot be undone.`
    );
    if (!confirmed) return;
    setLoading(true);
    try {
      const res = await axios.delete(`${API}/admin/strategies/${strategyId}/data`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      const deleted = res.data?.deleted_alerts ?? 0;
      toast.success(`${strategyName} reset — ${deleted} alert${deleted === 1 ? '' : 's'} deleted`);
    } catch (e) {
      toast.error('Failed to reset strategy data');
    }
    setLoading(false);
  };

  const resetStrategyWebhook = async (strategyId, strategyName) => {
    const confirmed = window.confirm(
      `Reset webhook URLs for "${strategyName}"?\n\nAll existing webhook sources (TradingView/Chartink/external apps) using the current URLs will STOP working immediately. You will need to update them with the new URL.\n\nThis cannot be undone.`
    );
    if (!confirmed) return;
    setLoading(true);
    try {
      await axios.post(`${API}/admin/strategies/${strategyId}/reset-webhook`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success(`${strategyName} webhook URLs regenerated. Copy the new URLs now.`);
      // Refresh admin data to show new token
      fetchData();
    } catch (e) {
      toast.error('Failed to reset webhook');
    }
    setLoading(false);
  };

  const sendStrategyMessage = async (strategyId, alertType) => {
    const stocksMap = alertType === 'buy' ? stratMsgBuy : stratMsgSell;
    const stocks = (stocksMap[strategyId] || '').trim();
    if (!stocks) {
      toast.error('Please enter stock symbol(s)');
      return;
    }
    setLoading(true);
    try {
      await axios.post(`${API}/admin/strategy-message/${strategyId}`, 
        { stocks, alert_type: alertType },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success(`${alertType.toUpperCase()} alert sent: ${stocks}`);
      if (alertType === 'buy') {
        setStratMsgBuy(prev => ({ ...prev, [strategyId]: '' }));
      } else {
        setStratMsgSell(prev => ({ ...prev, [strategyId]: '' }));
      }
    } catch (e) {
      toast.error(e.response?.data?.detail || `Failed to send ${alertType} alert`);
    }
    setLoading(false);
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return '-';
    try {
      return new Date(dateStr).toLocaleDateString();
    } catch {
      return dateStr;
    }
  };

  return (
    <div className="admin-dashboard" data-testid="admin-dashboard">
      <Toaster position="top-right" theme="dark" />
      {/* Admin Header */}
      <header className="admin-header">
        <div className="flex items-center gap-3">
          <img src="/logo.png" alt="Logo" className="w-10 h-10 logo-blend" />
          <div>
            <h1 className="font-mono text-lg font-bold text-[#d4a84b]">ADMIN PANEL</h1>
            <p className="text-xs text-[#71717a]">{user?.email}</p>
          </div>
        </div>
        <Button onClick={logout} variant="ghost" className="text-[#71717a] hover:text-white">
          <LogOut className="w-4 h-4 mr-2" /> Logout
        </Button>
      </header>

      {/* Stats */}
      <div className="admin-stats">
        <div className="stat-card">
          <Users className="w-8 h-8 text-[#d4a84b]" />
          <div>
            <p className="text-2xl font-bold text-white">{stats.total_users || 0}</p>
            <p className="text-xs text-[#71717a]">Total Users</p>
          </div>
        </div>
        <div className="stat-card">
          <Check className="w-8 h-8 text-green-400" />
          <div>
            <p className="text-2xl font-bold text-white">{stats.active_users || 0}</p>
            <p className="text-xs text-[#71717a]">Active</p>
          </div>
        </div>
        <div className="stat-card">
          <Clock className="w-8 h-8 text-yellow-400" />
          <div>
            <p className="text-2xl font-bold text-white">{stats.pending_verification || 0}</p>
            <p className="text-xs text-[#71717a]">Pending</p>
          </div>
        </div>
        <div className="stat-card">
          <X className="w-8 h-8 text-red-400" />
          <div>
            <p className="text-2xl font-bold text-white">{stats.expired_users || 0}</p>
            <p className="text-xs text-[#71717a]">Expired</p>
          </div>
        </div>
      </div>

      {/* Tabs */}
      <div className="admin-tabs">
        <button 
          className={`admin-tab ${activeTab === 'users' ? 'active' : ''}`}
          onClick={() => setActiveTab('users')}
        >
          <Users className="w-4 h-4" /> Users
        </button>
        <button 
          className={`admin-tab ${activeTab === 'message' ? 'active' : ''}`}
          onClick={() => setActiveTab('message')}
        >
          <Send className="w-4 h-4" /> Messages
        </button>
        <button 
          className={`admin-tab ${activeTab === 'webhook' ? 'active' : ''}`}
          onClick={() => setActiveTab('webhook')}
        >
          <Zap className="w-4 h-4" /> Webhooks
        </button>
        <button 
          className={`admin-tab ${activeTab === 'strategies' ? 'active' : ''}`}
          onClick={() => setActiveTab('strategies')}
        >
          <Layers className="w-4 h-4" /> Strategies
        </button>
        <button 
          className={`admin-tab ${activeTab === 'payment' ? 'active' : ''}`}
          onClick={() => setActiveTab('payment')}
        >
          <QrCode className="w-4 h-4" /> QR/Bank
        </button>
      </div>

      {/* Tab Content */}
      <div className="admin-content">
        {activeTab === 'users' && (
          <div className="users-list" data-testid="users-list">
            <ScrollArea className="h-[500px]">
              {users.length === 0 ? (
                <p className="text-center text-[#71717a] py-8">No users yet</p>
              ) : (
                <table className="w-full">
                  <thead>
                    <tr className="text-left text-xs text-[#71717a] border-b border-[#d4a84b]/20">
                      <th className="pb-2 px-2">User</th>
                      <th className="pb-2 px-2">Status</th>
                      <th className="pb-2 px-2">Plan</th>
                      <th className="pb-2 px-2">Expires</th>
                      <th className="pb-2 px-2">Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {users.map(u => (
                      <tr key={u.id} className="border-b border-[#d4a84b]/10 hover:bg-[#d4a84b]/5">
                        <td className="py-3 px-2">
                          <div className="text-sm text-white">{u.name || u.mobile || u.email}</div>
                          <div className="text-xs text-[#71717a]">{u.mobile || u.email}</div>
                        </td>
                        <td className="py-3 px-2">
                          <span className={`status-badge ${u.membership_status}`}>
                            {u.membership_status}
                          </span>
                        </td>
                        <td className="py-3 px-2 text-sm text-[#71717a]">{u.plan_type || '-'}</td>
                        <td className="py-3 px-2 text-sm text-[#71717a]">{formatDate(u.plan_end_date)}</td>
                        <td className="py-3 px-2">
                          <div className="flex gap-1">
                            {u.membership_status === 'pending_payment' && (
                              <Button size="sm" onClick={() => verifyUser(u.id)} className="bg-green-600 hover:bg-green-700 text-xs">
                                Verify
                              </Button>
                            )}
                            <Button size="sm" variant="outline" onClick={() => extendMembership(u.id, EXTEND_DAYS_DEFAULT)} className="text-xs border-[#d4a84b]/30">
                              +30d
                            </Button>
                            <Button size="sm" variant="ghost" onClick={() => deleteUser(u.id)} className="text-red-400 hover:text-red-300 text-xs">
                              <Trash2 className="w-3 h-3" />
                            </Button>
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              )}
            </ScrollArea>
          </div>
        )}

        {activeTab === 'message' && (
          <div className="message-form" data-testid="message-form">
            {/* Welcome Message Section */}
            <div className="mb-8 p-4 bg-[#0f1f35] rounded-lg border border-[#d4a84b]/20">
              <h3 className="text-lg font-semibold text-white mb-2">Welcome Message</h3>
              <p className="text-sm text-[#71717a] mb-4">This message is shown to users when they first login each session.</p>
              <textarea
                value={welcomeMessage}
                onChange={(e) => setWelcomeMessage(e.target.value)}
                placeholder="Welcome to Wolffs InstaAlerts! Stay tuned for profitable alerts..."
                className="w-full h-24 bg-[#0a1628] border border-[#d4a84b]/30 rounded-lg p-3 text-white resize-none text-sm"
              />
              <Button
                onClick={saveWelcomeMessage}
                className="mt-3 bg-[#d4a84b] text-[#0a1628] hover:bg-[#c49a40]"
                disabled={loading}
              >
                Save Welcome Message
              </Button>
            </div>

            {/* Broadcast Message Section */}
            <div className="mb-8 p-4 bg-[#0f1f35] rounded-lg border border-[#d4a84b]/20">
              <h3 className="text-lg font-semibold text-white mb-2">Broadcast Message</h3>
              <p className="text-sm text-[#71717a] mb-4">Send a popup message to ALL users (including non-members).</p>
              <textarea
                value={adminMessage}
                onChange={(e) => setAdminMessage(e.target.value)}
                placeholder="Type your broadcast message here..."
                className="w-full h-24 bg-[#0a1628] border border-[#d4a84b]/30 rounded-lg p-3 text-white resize-none text-sm"
                data-testid="admin-message-input"
              />
              <Button
                onClick={sendMessage}
                className="mt-3 bg-[#d4a84b] text-[#0a1628] hover:bg-[#c49a40]"
                disabled={loading || !adminMessage.trim()}
                data-testid="send-message-btn"
              >
                <Send className="w-4 h-4 mr-2" /> Send to All
              </Button>
            </div>

            {/* Private Message Section */}
            <div className="p-4 bg-[#0f1f35] rounded-lg border border-blue-500/20">
              <h3 className="text-lg font-semibold text-white mb-2">Private Message</h3>
              <p className="text-sm text-[#71717a] mb-4">Send a message to a specific user only.</p>
              
              <select
                value={selectedUserId}
                onChange={(e) => setSelectedUserId(e.target.value)}
                className="w-full bg-[#0a1628] border border-blue-500/30 rounded-lg p-3 text-white mb-3 text-sm"
              >
                <option value="">Select a user...</option>
                {users.map(u => (
                  <option key={u.id} value={u.id}>
                    {u.name || u.mobile || u.email} ({u.membership_status})
                  </option>
                ))}
              </select>
              
              <textarea
                value={privateMessage}
                onChange={(e) => setPrivateMessage(e.target.value)}
                placeholder="Type your private message here..."
                className="w-full h-24 bg-[#0a1628] border border-blue-500/30 rounded-lg p-3 text-white resize-none text-sm"
              />
              <Button
                onClick={sendPrivateMessage}
                className="mt-3 bg-blue-600 hover:bg-blue-700 text-white"
                disabled={loading || !privateMessage.trim() || !selectedUserId}
              >
                <Send className="w-4 h-4 mr-2" /> Send Private
              </Button>
            </div>

            {/* Test Alerts Section */}
            <div className="mt-8 p-4 bg-[#0f1f35] rounded-lg border border-purple-500/20">
              <h3 className="text-lg font-semibold text-white mb-2">🧪 Test Alerts</h3>
              <p className="text-sm text-[#71717a] mb-4">Send test BUY/SELL alerts to verify the system is working.</p>
              
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-3">
                  <Input
                    value={testBuyStock}
                    onChange={(e) => setTestBuyStock(e.target.value.toUpperCase())}
                    placeholder="Stock symbol (e.g., RELIANCE)"
                    className="bg-[#0a1628] border-green-500/30 text-white"
                  />
                  <Button
                    onClick={() => sendTestAlert('buy')}
                    className="w-full bg-green-600 hover:bg-green-700 text-white"
                    disabled={loading || !testBuyStock.trim()}
                    data-testid="test-buy-btn"
                  >
                    <Zap className="w-4 h-4 mr-2" /> Send BUY Alert
                  </Button>
                </div>
                
                <div className="space-y-3">
                  <Input
                    value={testSellStock}
                    onChange={(e) => setTestSellStock(e.target.value.toUpperCase())}
                    placeholder="Stock symbol (e.g., TCS)"
                    className="bg-[#0a1628] border-red-500/30 text-white"
                  />
                  <Button
                    onClick={() => sendTestAlert('sell')}
                    className="w-full bg-red-600 hover:bg-red-700 text-white"
                    disabled={loading || !testSellStock.trim()}
                    data-testid="test-sell-btn"
                  >
                    <Zap className="w-4 h-4 mr-2" /> Send SELL Alert
                  </Button>
                </div>
              </div>
            </div>
          </div>
        )}

        {activeTab === 'webhook' && (
          <div className="webhook-settings" data-testid="webhook-settings">

            {/* ===== TRADINGVIEW SETUP GUIDE ===== */}
            <h3 className="text-lg font-semibold text-white mb-4">TradingView Setup Guide</h3>
            <p className="text-sm text-[#71717a] mb-4">Use the <strong className="text-cyan-400">AUTO</strong> webhook URL from the Strategies tab. It auto-detects BUY/SELL/EXIT from TradingView alerts.</p>

            <div className="bg-[#0f1f35] border border-cyan-500/20 rounded-xl p-5 mb-6">
              <h4 className="text-sm font-bold text-cyan-400 mb-4 flex items-center gap-2">
                <Zap className="w-4 h-4" /> How to Connect TradingView
              </h4>

              <div className="space-y-4 text-sm text-[#a1a1aa]">
                <div className="flex gap-3">
                  <span className="bg-cyan-500/20 text-cyan-400 font-bold w-6 h-6 rounded-full flex items-center justify-center shrink-0 text-xs">1</span>
                  <div>
                    <p className="text-white font-medium mb-1">Copy the AUTO Webhook URL</p>
                    <p>Go to the <strong className="text-[#d4a84b]">Strategies</strong> tab and copy the <span className="text-cyan-400 font-mono">AUTO</span> URL for the strategy you want to connect.</p>
                  </div>
                </div>

                <div className="flex gap-3">
                  <span className="bg-cyan-500/20 text-cyan-400 font-bold w-6 h-6 rounded-full flex items-center justify-center shrink-0 text-xs">2</span>
                  <div>
                    <p className="text-white font-medium mb-1">Create an Alert in TradingView</p>
                    <p>Open your strategy chart, click <strong>Create Alert</strong>, and check the <strong>"Webhook URL"</strong> checkbox at the bottom.</p>
                  </div>
                </div>

                <div className="flex gap-3">
                  <span className="bg-cyan-500/20 text-cyan-400 font-bold w-6 h-6 rounded-full flex items-center justify-center shrink-0 text-xs">3</span>
                  <div>
                    <p className="text-white font-medium mb-1">Paste the AUTO URL</p>
                    <p>Paste the AUTO webhook URL in the webhook URL field.</p>
                  </div>
                </div>

                <div className="flex gap-3">
                  <span className="bg-cyan-500/20 text-cyan-400 font-bold w-6 h-6 rounded-full flex items-center justify-center shrink-0 text-xs">4</span>
                  <div>
                    <p className="text-white font-medium mb-1">Set the Alert Message</p>
                    <p className="mb-2">In the <strong>"Message"</strong> field, paste one of the JSON templates below:</p>
                  </div>
                </div>
              </div>

              {/* JSON Templates */}
              <div className="mt-4 space-y-3">
                <div className="bg-[#0a1628] rounded-lg p-3 border border-cyan-500/10">
                  <div className="flex items-center justify-between mb-2">
                    <span className="text-xs text-cyan-400 font-bold uppercase">For Strategy Alerts (auto BUY/SELL)</span>
                    <Button
                      onClick={() => {
                        navigator.clipboard.writeText('{\n  "stocks": "{{ticker}}",\n  "action": "{{strategy.order.action}}",\n  "market_position": "{{strategy.market_position}}",\n  "scan_name": "{{strategy.order.id}}"\n}');
                        toast.success('Strategy JSON copied!');
                      }}
                      variant="ghost" size="sm" className="text-[#71717a] hover:text-cyan-400 text-xs h-6 px-2"
                    >
                      <Copy className="w-3 h-3 mr-1" /> Copy
                    </Button>
                  </div>
                  <pre className="text-xs text-green-400 font-mono leading-relaxed whitespace-pre">{`{
  "stocks": "{{ticker}}",
  "action": "{{strategy.order.action}}",
  "market_position": "{{strategy.market_position}}",
  "scan_name": "{{strategy.order.id}}"
}`}</pre>
                  <p className="text-[10px] text-[#71717a] mt-2"><code className="text-cyan-400/60">market_position</code> becomes "flat" when exiting — the app auto-detects this as an EXIT signal and shows it in the correct section.</p>
                </div>

                <div className="bg-[#0a1628] rounded-lg p-3 border border-cyan-500/10">
                  <div className="flex items-center justify-between mb-2">
                    <span className="text-xs text-cyan-400 font-bold uppercase">For Manual/Indicator Alerts</span>
                    <Button
                      onClick={() => {
                        navigator.clipboard.writeText('{\n  "stocks": "{{ticker}}",\n  "action": "buy",\n  "scan_name": "My Alert Name"\n}');
                        toast.success('Manual JSON copied!');
                      }}
                      variant="ghost" size="sm" className="text-[#71717a] hover:text-cyan-400 text-xs h-6 px-2"
                    >
                      <Copy className="w-3 h-3 mr-1" /> Copy
                    </Button>
                  </div>
                  <pre className="text-xs text-amber-400 font-mono leading-relaxed whitespace-pre">{`{
  "stocks": "{{ticker}}",
  "action": "buy",
  "scan_name": "My Alert Name"
}`}</pre>
                  <p className="text-[10px] text-[#71717a] mt-2">Change <code className="text-amber-400/60">"buy"</code> to <code className="text-amber-400/60">"sell"</code> or <code className="text-amber-400/60">"exit"</code> as needed.</p>
                </div>
              </div>

              {/* Supported actions */}
              <div className="mt-4 p-3 bg-[#d4a84b]/5 rounded-lg border border-[#d4a84b]/20">
                <p className="text-xs text-[#d4a84b] font-semibold mb-2">Supported Action Values:</p>
                <div className="grid grid-cols-3 gap-2 text-xs">
                  <div>
                    <span className="text-green-400 font-bold">BUY:</span>
                    <span className="text-[#a1a1aa]"> buy, long</span>
                  </div>
                  <div>
                    <span className="text-red-400 font-bold">SELL:</span>
                    <span className="text-[#a1a1aa]"> sell, short</span>
                  </div>
                  <div>
                    <span className="text-purple-400 font-bold">EXIT:</span>
                    <span className="text-[#a1a1aa]"> exit, close</span>
                  </div>
                </div>
                <p className="text-[10px] text-[#71717a] mt-2">Exchange prefixes like NSE: or BSE: are stripped automatically.</p>
              </div>
            </div>

            {/* ===== CHARTINK LEGACY SECTION ===== */}
            <h3 className="text-lg font-semibold text-white mb-4">Chartink Webhook URLs (Legacy)</h3>
            <p className="text-sm text-[#71717a] mb-4">These legacy URLs send alerts to ALL users (not strategy-filtered). For strategy-specific webhooks, use the URLs in the Strategies tab.</p>
            
            {/* BUY Webhook */}
            <div className="bg-[#0f1f35] border border-green-500/30 rounded-lg p-4 mb-4">
              <label className="text-xs text-green-400 uppercase tracking-wider mb-2 block flex items-center gap-2">
                <div className="w-2 h-2 rounded-full bg-green-500" />
                BUY Alert Webhook URL
              </label>
              <div className="flex gap-2">
                <div className="webhook-url flex-1 !text-green-400" data-testid="buy-webhook-url">
                  {`${BACKEND_URL}/api/webhook/chartink/buy`}
                </div>
                <Button
                  onClick={() => {
                    navigator.clipboard.writeText(`${BACKEND_URL}/api/webhook/chartink/buy`);
                    toast.success('BUY Webhook URL copied!');
                  }}
                  variant="outline"
                  size="icon"
                  className="shrink-0 border-green-500/30 hover:border-green-500 hover:text-green-400"
                >
                  <Copy className="w-4 h-4" />
                </Button>
              </div>
            </div>

            {/* SELL Webhook */}
            <div className="bg-[#0f1f35] border border-red-500/30 rounded-lg p-4 mb-6">
              <label className="text-xs text-red-400 uppercase tracking-wider mb-2 block flex items-center gap-2">
                <div className="w-2 h-2 rounded-full bg-red-500" />
                SELL Alert Webhook URL
              </label>
              <div className="flex gap-2">
                <div className="webhook-url flex-1 !text-red-400" data-testid="sell-webhook-url">
                  {`${BACKEND_URL}/api/webhook/chartink/sell`}
                </div>
                <Button
                  onClick={() => {
                    navigator.clipboard.writeText(`${BACKEND_URL}/api/webhook/chartink/sell`);
                    toast.success('SELL Webhook URL copied!');
                  }}
                  variant="outline"
                  size="icon"
                  className="shrink-0 border-red-500/30 hover:border-red-500 hover:text-red-400"
                >
                  <Copy className="w-4 h-4" />
                </Button>
              </div>
            </div>
            
            <div className="p-4 bg-[#d4a84b]/10 rounded-lg border border-[#d4a84b]/30">
              <h4 className="text-sm font-semibold text-[#d4a84b] mb-2">Chartink Setup Instructions:</h4>
              <ol className="text-sm text-[#a1a1aa] space-y-2 list-decimal list-inside">
                <li>Create two separate scanners in Chartink - one for BUY signals, one for SELL signals</li>
                <li>For your BUY scanner, paste the <span className="text-green-400 font-mono">BUY Webhook URL</span></li>
                <li>For your SELL scanner, paste the <span className="text-red-400 font-mono">SELL Webhook URL</span></li>
                <li>Alerts will automatically appear in the correct section on user dashboards</li>
                <li><strong className="text-white">For strategy-specific alerts</strong>, use the webhook URLs from the <span className="text-[#d4a84b]">Strategies</span> tab instead</li>
              </ol>
            </div>
          </div>
        )}

        {activeTab === 'strategies' && (
          <div className="strategies-settings" data-testid="strategies-settings">
            <h3 className="text-lg font-semibold text-white mb-2">Strategy Management</h3>
            <p className="text-sm text-[#71717a] mb-6">Edit strategy names, descriptions, and types. Each strategy has its own webhook URLs.</p>

            {/* Server Outbound IP — for MSG91 whitelisting */}
            <div className="mb-6 p-4 bg-[#0f1f35] rounded-xl border border-cyan-500/30">
              <div className="flex items-start justify-between gap-3 flex-wrap">
                <div className="flex-1 min-w-0">
                  <h4 className="text-base font-semibold text-cyan-400 mb-1">MSG91 Server IP Whitelist</h4>
                  <p className="text-xs text-[#a1a1aa] mb-2">
                    Add this server&apos;s outbound IP to your MSG91 IP whitelist at{' '}
                    <a href="https://control.msg91.com/app/security/ip-whitelist" target="_blank" rel="noreferrer" className="text-cyan-400 underline">MSG91 Security Settings</a>{' '}
                    to enable OTP sending.
                  </p>
                </div>
                <Button
                  onClick={fetchServerIp}
                  disabled={loadingIp}
                  className="bg-cyan-500/20 text-cyan-400 hover:bg-cyan-500/30 border border-cyan-500/40 text-xs"
                  data-testid="show-server-ip-btn"
                >
                  {loadingIp ? 'Fetching…' : (serverIp ? 'Refresh IP' : 'Show Server IP')}
                </Button>
              </div>
              {serverIp && (
                <div className="mt-3 space-y-2" data-testid="server-ip-display">
                  {Object.entries(serverIp).map(([source, ip]) => (
                    <div key={source} className="flex items-center gap-2 text-xs">
                      <span className="text-[#71717a] w-24 shrink-0 uppercase tracking-wider">{source}</span>
                      <code className="flex-1 bg-[#0a1628] text-cyan-300 px-3 py-1.5 rounded border border-cyan-500/20 font-mono break-all">
                        {ip}
                      </code>
                      <Button
                        onClick={() => { navigator.clipboard.writeText(ip); toast.success('IP copied!'); }}
                        variant="ghost"
                        size="sm"
                        className="p-1 text-[#71717a] hover:text-cyan-400"
                        data-testid={`copy-ip-${source}`}
                      >
                        <Copy className="w-3.5 h-3.5" />
                      </Button>
                    </div>
                  ))}
                  <p className="text-[10px] text-[#71717a] mt-2 italic">
                    Tip: If MSG91 keeps returning Error 418, the server IP may have rotated. Click &quot;Refresh IP&quot; and re-whitelist.
                  </p>
                </div>
              )}
            </div>

            {/* MSG91 Diagnostic Tool */}
            <div className="mb-6 p-4 bg-[#0f1f35] rounded-xl border border-orange-500/30">
              <h4 className="text-base font-semibold text-orange-400 mb-1">MSG91 OTP Diagnostic</h4>
              <p className="text-xs text-[#a1a1aa] mb-3">
                Send a real test OTP and view the complete MSG91 API response, including config, request payload, and raw response. Use this if OTPs aren&apos;t being received.
              </p>
              <div className="flex gap-2 items-center mb-3">
                <Input
                  type="tel"
                  placeholder="10-digit test mobile"
                  value={diagMobile}
                  onChange={(e) => setDiagMobile(e.target.value.replace(/\D/g, '').slice(0, 10))}
                  className="flex-1 bg-[#0a1628] border-orange-500/30 text-white text-sm"
                  maxLength={10}
                  data-testid="msg91-diag-mobile"
                />
                <Button
                  onClick={runMsg91Diagnose}
                  disabled={diagLoading || diagMobile.length !== 10}
                  className="bg-orange-500/20 text-orange-400 hover:bg-orange-500/30 border border-orange-500/40 text-xs"
                  data-testid="msg91-diag-run"
                >
                  {diagLoading ? 'Sending…' : 'Send Test OTP'}
                </Button>
              </div>
              {diagResult && (
                <pre className="text-[10px] bg-[#0a1628] border border-orange-500/20 rounded p-3 overflow-x-auto text-orange-200 max-h-96 overflow-y-auto" data-testid="msg91-diag-result">
                  {JSON.stringify(diagResult, null, 2)}
                </pre>
              )}
              <p className="text-[10px] text-[#71717a] mt-2 italic">
                Cross-check the response against your MSG91 dashboard at{' '}
                <a href="https://control.msg91.com/app/reports/sms/logs" target="_blank" rel="noreferrer" className="text-orange-400 underline">SMS Reports</a>{' '}
                to see actual delivery status (Delivered / DLT-failed / Insufficient balance).
              </p>
            </div>

            <div className="space-y-6">
              {adminStrategies.map((strat, idx) => {
                const isEditing = !!editingStrategy[strat.id];
                const edit = editingStrategy[strat.id] || {};
                const stratType = isEditing ? edit.strategy_type : strat.strategy_type;
                
                return (
                  <div key={strat.id} className="bg-[#0f1f35] rounded-xl border border-[#d4a84b]/20 p-4" data-testid={`admin-strategy-${idx}`}>
                    {/* Strategy Name */}
                    <div className="flex items-center justify-between mb-3">
                      {isEditing ? (
                        <Input
                          value={edit.name}
                          onChange={(e) => updateEditField(strat.id, 'name', e.target.value)}
                          className="bg-[#0a1628] border-[#d4a84b]/30 text-white font-semibold text-lg"
                          placeholder="Strategy Name"
                        />
                      ) : (
                        <div className="flex items-center gap-2">
                          <span className="text-[10px] font-bold px-2 py-0.5 rounded bg-[#d4a84b]/15 text-[#d4a84b] ring-1 ring-[#d4a84b]/30 shrink-0" data-testid={`stg-no-${idx}`}>
                            STG-{strat.strategy_number || '?'}
                          </span>
                          <h4 className="text-lg font-semibold text-white">{strat.name}</h4>
                        </div>
                      )}
                      {!isEditing && (
                        <div className="flex items-center gap-2 flex-wrap">
                          <Button size="sm" variant="outline" onClick={() => startEditingStrategy(strat)} className="text-xs border-[#d4a84b]/30 text-[#d4a84b]">
                            Edit
                          </Button>
                          <Button
                            size="sm"
                            variant="outline"
                            onClick={() => resetStrategyData(strat.id, strat.name)}
                            disabled={loading}
                            className="text-xs border-red-500/30 text-red-400 hover:bg-red-500/10 hover:text-red-300"
                            data-testid={`reset-strategy-${idx}`}
                          >
                            Reset Data
                          </Button>
                          <Button
                            size="sm"
                            variant="outline"
                            onClick={() => resetStrategyWebhook(strat.id, strat.name)}
                            disabled={loading}
                            className="text-xs border-amber-500/30 text-amber-400 hover:bg-amber-500/10 hover:text-amber-300"
                            data-testid={`reset-webhook-${idx}`}
                          >
                            Reset Webhook
                          </Button>
                        </div>
                      )}
                    </div>

                    {/* Description */}
                    {isEditing ? (
                      <textarea
                        value={edit.description}
                        onChange={(e) => updateEditField(strat.id, 'description', e.target.value)}
                        className="w-full h-20 bg-[#0a1628] border border-[#d4a84b]/30 rounded-lg p-3 text-white resize-none text-sm mb-3"
                        placeholder="Strategy description..."
                      />
                    ) : (
                      <p className="text-sm text-[#a1a1aa] mb-3">{strat.description}</p>
                    )}

                    {/* Strategy Type Selection */}
                    <div className="mb-4">
                      <label className="text-xs text-[#71717a] uppercase tracking-wider mb-2 block">Strategy Type</label>
                      {isEditing ? (
                        <div className="grid grid-cols-3 sm:grid-cols-7 gap-2">
                          {['normal', 'reversal', 'exit', 'message', 'avg', 'news', 'pair'].map(type => (
                            <button
                              key={type}
                              onClick={() => updateEditField(strat.id, 'strategy_type', type)}
                              className={`py-2 px-3 rounded-lg text-xs font-bold uppercase transition-all border ${
                                edit.strategy_type === type
                                  ? type === 'normal' ? 'bg-[#71717a]/30 border-[#71717a] text-white'
                                    : type === 'reversal' ? 'bg-amber-500/30 border-amber-500 text-amber-400'
                                    : type === 'exit' ? 'bg-purple-500/30 border-purple-500 text-purple-400'
                                    : type === 'message' ? 'bg-cyan-500/30 border-cyan-500 text-cyan-400'
                                    : type === 'avg' ? 'bg-orange-500/30 border-orange-500 text-orange-400'
                                    : type === 'news' ? 'bg-blue-500/30 border-blue-500 text-blue-400'
                                    : 'bg-fuchsia-500/30 border-fuchsia-500 text-fuchsia-400'
                                  : 'border-[#1e293b] text-[#71717a] hover:border-[#404040]'
                              }`}
                            >
                              {type}
                            </button>
                          ))}
                        </div>
                      ) : (
                        <span className={`text-xs px-3 py-1 rounded-full font-bold ${getStrategyTypeClass(stratType)}`}>
                          {stratType.toUpperCase()}
                        </span>
                      )}
                    </div>

                    {/* Save/Cancel buttons when editing */}
                    {isEditing && (
                      <div className="flex gap-2 mb-4">
                        <Button
                          onClick={() => updateStrategy(strat.id)}
                          className="bg-[#d4a84b] text-[#0a1628] hover:bg-[#c49a40] text-sm"
                          disabled={loading}
                        >
                          Save Changes
                        </Button>
                        <Button
                          onClick={() => setEditingStrategy(prev => { const n = {...prev}; delete n[strat.id]; return n; })}
                          variant="outline"
                          className="text-sm border-[#404040]"
                        >
                          Cancel
                        </Button>
                      </div>
                    )}

                    {/* Webhook URLs or Message Windows depending on type */}
                    {stratType === 'message' ? (
                      <div className="space-y-3 border-t border-white/5 pt-3">
                        <label className="text-xs text-[#71717a] uppercase tracking-wider">Send Manual Alerts</label>
                        
                        {/* BUY Message */}
                        <div className="bg-[#0a1628] rounded-lg p-3 border border-green-500/20">
                          <label className="text-xs text-green-400 font-bold mb-2 block">BUY Alert</label>
                          <div className="flex gap-2">
                            <Input
                              value={stratMsgBuy[strat.id] || ''}
                              onChange={(e) => setStratMsgBuy(prev => ({ ...prev, [strat.id]: e.target.value.toUpperCase() }))}
                              placeholder="Stock symbol(s), e.g. RELIANCE, TCS"
                              className="bg-[#0f1f35] border-green-500/30 text-white text-sm"
                              data-testid={`strat-msg-buy-${idx}`}
                            />
                            <Button
                              onClick={() => sendStrategyMessage(strat.id, 'buy')}
                              className="bg-green-600 hover:bg-green-700 text-white shrink-0"
                              disabled={loading || !(stratMsgBuy[strat.id] || '').trim()}
                            >
                              <Send className="w-4 h-4" />
                            </Button>
                          </div>
                        </div>

                        {/* SELL Message */}
                        <div className="bg-[#0a1628] rounded-lg p-3 border border-red-500/20">
                          <label className="text-xs text-red-400 font-bold mb-2 block">SELL Alert</label>
                          <div className="flex gap-2">
                            <Input
                              value={stratMsgSell[strat.id] || ''}
                              onChange={(e) => setStratMsgSell(prev => ({ ...prev, [strat.id]: e.target.value.toUpperCase() }))}
                              placeholder="Stock symbol(s), e.g. INFY, HDFC"
                              className="bg-[#0f1f35] border-red-500/30 text-white text-sm"
                              data-testid={`strat-msg-sell-${idx}`}
                            />
                            <Button
                              onClick={() => sendStrategyMessage(strat.id, 'sell')}
                              className="bg-red-600 hover:bg-red-700 text-white shrink-0"
                              disabled={loading || !(stratMsgSell[strat.id] || '').trim()}
                            >
                              <Send className="w-4 h-4" />
                            </Button>
                          </div>
                        </div>
                      </div>
                    ) : (
                    <div className="space-y-2 border-t border-white/5 pt-3">
                      <label className="text-xs text-[#71717a] uppercase tracking-wider">Webhook URLs</label>
                      
                      {/* BUY URL */}
                      <div className="flex gap-2 items-center">
                        <span className="text-xs text-green-400 w-10 shrink-0 font-bold">BUY</span>
                        <div className="webhook-url flex-1 !text-xs !py-1.5 text-green-400/80 break-all">
                          {`${BACKEND_URL}/api/webhook/strategy/STG-${strat.strategy_number}/buy?token=${strat.webhook_token || ''}`}
                        </div>
                        <Button
                          onClick={() => { navigator.clipboard.writeText(`${BACKEND_URL}/api/webhook/strategy/STG-${strat.strategy_number}/buy?token=${strat.webhook_token || ''}`); toast.success('BUY URL copied!'); }}
                          variant="ghost" size="sm" className="p-1 text-[#71717a] hover:text-green-400"
                        >
                          <Copy className="w-3.5 h-3.5" />
                        </Button>
                      </div>

                      {/* SELL URL — shown for all types except message (AVG now supports sell averaging) */}
                      <div className="flex gap-2 items-center">
                        <span className="text-xs text-red-400 w-10 shrink-0 font-bold">SELL</span>
                        <div className="webhook-url flex-1 !text-xs !py-1.5 text-red-400/80 break-all">
                          {`${BACKEND_URL}/api/webhook/strategy/STG-${strat.strategy_number}/sell?token=${strat.webhook_token || ''}`}
                        </div>
                        <Button
                          onClick={() => { navigator.clipboard.writeText(`${BACKEND_URL}/api/webhook/strategy/STG-${strat.strategy_number}/sell?token=${strat.webhook_token || ''}`); toast.success('SELL URL copied!'); }}
                          variant="ghost" size="sm" className="p-1 text-[#71717a] hover:text-red-400"
                        >
                          <Copy className="w-3.5 h-3.5" />
                        </Button>
                      </div>

                      {/* BUY EXIT + SELL EXIT URLs — for exit, avg, and pair types */}
                      {(stratType === 'exit' || stratType === 'avg' || stratType === 'pair') && (
                        <>
                          <div className="flex gap-2 items-center">
                            <span className="text-[10px] text-purple-400 w-10 shrink-0 font-bold leading-tight">BUY<br/>EXIT</span>
                            <div className="webhook-url flex-1 !text-xs !py-1.5 text-purple-400/80 break-all">
                              {`${BACKEND_URL}/api/webhook/strategy/STG-${strat.strategy_number}/buy-exit?token=${strat.webhook_token || ''}`}
                            </div>
                            <Button
                              onClick={() => { navigator.clipboard.writeText(`${BACKEND_URL}/api/webhook/strategy/STG-${strat.strategy_number}/buy-exit?token=${strat.webhook_token || ''}`); toast.success('BUY EXIT URL copied!'); }}
                              variant="ghost" size="sm" className="p-1 text-[#71717a] hover:text-purple-400"
                            >
                              <Copy className="w-3.5 h-3.5" />
                            </Button>
                          </div>
                          <div className="flex gap-2 items-center">
                            <span className="text-[10px] text-pink-400 w-10 shrink-0 font-bold leading-tight">SELL<br/>EXIT</span>
                            <div className="webhook-url flex-1 !text-xs !py-1.5 text-pink-400/80 break-all">
                              {`${BACKEND_URL}/api/webhook/strategy/STG-${strat.strategy_number}/sell-exit?token=${strat.webhook_token || ''}`}
                            </div>
                            <Button
                              onClick={() => { navigator.clipboard.writeText(`${BACKEND_URL}/api/webhook/strategy/STG-${strat.strategy_number}/sell-exit?token=${strat.webhook_token || ''}`); toast.success('SELL EXIT URL copied!'); }}
                              variant="ghost" size="sm" className="p-1 text-[#71717a] hover:text-pink-400"
                            >
                              <Copy className="w-3.5 h-3.5" />
                            </Button>
                          </div>
                        </>
                      )}

                      {/* AUTO URL — hidden for news and pair */}
                      {stratType !== 'news' && stratType !== 'pair' && (
                      <>
                      <div className="flex gap-2 items-center">
                        <span className="text-xs text-cyan-400 w-10 shrink-0 font-bold">AUTO</span>
                        <div className="webhook-url flex-1 !text-xs !py-1.5 text-cyan-400/80 break-all">
                          {`${BACKEND_URL}/api/webhook/strategy/STG-${strat.strategy_number}/auto?token=${strat.webhook_token || ''}`}
                        </div>
                        <Button
                          onClick={() => { navigator.clipboard.writeText(`${BACKEND_URL}/api/webhook/strategy/STG-${strat.strategy_number}/auto?token=${strat.webhook_token || ''}`); toast.success('AUTO URL copied!'); }}
                          variant="ghost" size="sm" className="p-1 text-[#71717a] hover:text-cyan-400"
                        >
                          <Copy className="w-3.5 h-3.5" />
                        </Button>
                      </div>
                      <p className="text-[10px] text-[#71717a] mt-1 ml-12">AUTO detects BUY/SELL/EXIT from the request body. Use with TradingView strategy alerts.</p>
                      </>
                      )}
                      {stratType === 'news' && (
                        <div className="mt-2 p-3 bg-blue-500/10 border border-blue-500/30 rounded-lg">
                          <p className="text-[11px] text-blue-300 font-semibold mb-1">📰 News Webhook Payload Format</p>
                          <p className="text-[10px] text-[#a1a1aa] leading-relaxed">External app should POST JSON with: <code className="text-blue-300">stock</code>, <code className="text-blue-300">headline</code>, <code className="text-blue-300">summary</code> (3 lines, newline-separated), <code className="text-blue-300">source</code>, <code className="text-blue-300">date</code>, <code className="text-blue-300">time</code>. BUY URL = bullish news; SELL URL = bearish news.</p>
                        </div>
                      )}
                    </div>
                    )}

                    {/* Per-Strategy Admin Broadcast (popup) — hidden for news (news IS the broadcast) */}
                    {stratType !== 'news' && (
                    <div className="mt-3 pt-3 border-t border-[#d4a84b]/10">
                      <label className="text-xs text-[#d4a84b] font-bold uppercase tracking-wider mb-2 block">Send Strategy Message (Popup)</label>
                      <p className="text-[11px] text-[#71717a] mb-2">Pops up only for users following this strategy. Goes to their Past Notifications as MSG after acknowledgement.</p>
                      <div className="flex gap-2">
                        <Input
                          value={stratBroadcast[strat.id] || ''}
                          onChange={(e) => setStratBroadcast(prev => ({ ...prev, [strat.id]: e.target.value }))}
                          placeholder="Type message for users following this strategy..."
                          className="bg-[#0a1628] border-[#d4a84b]/30 text-white text-sm"
                          data-testid={`strat-broadcast-input-${idx}`}
                        />
                        <Button
                          onClick={() => sendStrategyBroadcast(strat.id)}
                          className="bg-[#d4a84b] text-[#0a1628] hover:bg-[#c49a40] shrink-0"
                          disabled={loading || !(stratBroadcast[strat.id] || '').trim()}
                          data-testid={`strat-broadcast-send-${idx}`}
                        >
                          <Send className="w-4 h-4" />
                        </Button>
                      </div>
                    </div>
                    )}
                  </div>
                );
              })}
            </div>

            {/* Trial Strategy Access Control */}
            <div className="mt-6 p-4 bg-[#0f1f35] rounded-xl border border-[#d4a84b]/20 mb-6">
              <h4 className="text-sm font-semibold text-[#d4a84b] mb-2">Trial User Access</h4>
              <p className="text-xs text-[#71717a] mb-3">Select which strategies trial users can access. Leave all unchecked to give trial users access to everything.</p>
              <div className="flex flex-wrap gap-2 mb-3">
                {adminStrategies.map((strat) => (
                  <button
                    key={strat.id}
                    onClick={() => toggleAdminTrialId(strat.id)}
                    className={`text-xs font-semibold px-3 py-1.5 rounded-full transition-all border ${
                      adminTrialIds.includes(strat.id)
                        ? 'bg-[#d4a84b]/20 border-[#d4a84b] text-[#d4a84b]'
                        : 'border-[#1e293b] text-[#71717a] hover:border-[#404040]'
                    }`}
                    data-testid={`trial-strat-${strat.id}`}
                  >
                    {strat.name}
                  </button>
                ))}
              </div>
              <Button
                onClick={saveTrialStrategies}
                className="bg-[#d4a84b] text-[#0a1628] hover:bg-[#c49a40] text-xs"
                disabled={loading}
                data-testid="save-trial-strategies-btn"
              >
                Save Trial Access
              </Button>
            </div>

            {/* Paid Strategy Access Control */}
            <div className="mt-3 p-4 bg-[#0f1f35] rounded-xl border border-emerald-500/20 mb-6">
              <h4 className="text-sm font-semibold text-emerald-400 mb-2">Paid User Access</h4>
              <p className="text-xs text-[#71717a] mb-3">Select which strategies paid users can access. Leave all unchecked to give paid users access to all strategies.</p>
              <div className="flex flex-wrap gap-2 mb-3">
                {adminStrategies.map((strat) => (
                  <button
                    key={strat.id}
                    onClick={() => toggleAdminPaidId(strat.id)}
                    className={`text-xs font-semibold px-3 py-1.5 rounded-full transition-all border ${
                      adminPaidIds.includes(strat.id)
                        ? 'bg-emerald-500/20 border-emerald-500 text-emerald-400'
                        : 'border-[#1e293b] text-[#71717a] hover:border-[#404040]'
                    }`}
                    data-testid={`paid-strat-${strat.id}`}
                  >
                    {strat.name}
                  </button>
                ))}
              </div>
              <Button
                onClick={savePaidStrategies}
                className="bg-emerald-500 text-[#0a1628] hover:bg-emerald-600 text-xs"
                disabled={loading}
                data-testid="save-paid-strategies-btn"
              >
                Save Paid Access
              </Button>
            </div>

            {/* Type Explanation */}
            <div className="mt-6 p-4 bg-[#d4a84b]/10 rounded-lg border border-[#d4a84b]/30">
              <h4 className="text-sm font-semibold text-[#d4a84b] mb-3">Strategy Type Reference:</h4>
              <div className="space-y-3 text-sm text-[#a1a1aa]">
                <div>
                  <span className="text-white font-semibold">Normal:</span> Signals pass through as-is. Every signal creates an alert.
                </div>
                <div>
                  <span className="text-amber-400 font-semibold">Reversal:</span> Blocks duplicate same-side signals. E.g., if RELIANCE is already in BUY, another BUY for RELIANCE is blocked until a SELL signal switches it.
                </div>
                <div>
                  <span className="text-purple-400 font-semibold">Exit:</span> Per-side state. BUY signals tracked separately from SELL signals. <span className="text-purple-400">BUY EXIT</span> webhook clears buy state only and shows exit in BUY section. <span className="text-pink-400">SELL EXIT</span> webhook clears sell state only and shows exit in SELL section. Same-side duplicates are blocked until that side's exit fires.
                </div>
                <div>
                  <span className="text-cyan-400 font-semibold">Message:</span> No webhook. Admin sends BUY/SELL alerts manually via the input fields above.
                </div>
                <div>
                  <span className="text-orange-400 font-semibold">AVG:</span> Averaging strategy with separate BUY and SELL counters per stock. Each BUY shows as "RELIANCE", "RELIANCE (Avg 1)", "RELIANCE (Avg 2)"... Each SELL has its own counter. <span className="text-purple-400">BUY EXIT</span> clears buy averaging → "RELIANCE (Exit)" in BUY section. <span className="text-pink-400">SELL EXIT</span> clears sell averaging → "RELIANCE (Exit)" in SELL section.
                </div>
                <div>
                  <span className="text-blue-400 font-semibold">News:</span> Important News strategy. Webhook receives <code>stock</code>, <code>headline</code>, <code>summary</code> (3 lines), <code>source</code>, <code>date</code>, <code>time</code>. Pops up as full-screen NEWS card with BULLISH (buy URL) or BEARISH (sell URL) tag. After acknowledgement, appears in Past Notifications with expandable full text.
                </div>
              </div>
            </div>
          </div>
        )}

        {activeTab === 'payment' && (
          <div className="payment-settings" data-testid="payment-settings">
            <h3 className="text-lg font-semibold text-white mb-4">QR Code & Bank Details</h3>
            <p className="text-sm text-[#71717a] mb-4">Fallback payment option for users who prefer manual payment</p>
            
            <div className="space-y-4">
              <div>
                <label className="text-sm text-[#71717a] mb-2 block">QR Code Image</label>
                <input
                  type="file"
                  accept="image/*"
                  onChange={handleQRUpload}
                  className="w-full text-sm text-[#71717a]"
                />
                {paymentSettings.qr_code_base64 && (
                  <img src={paymentSettings.qr_code_base64} alt="QR" className="w-32 h-32 mt-2 rounded border border-[#d4a84b]/30" />
                )}
              </div>
              
              <div>
                <label className="text-sm text-[#71717a] mb-2 block">UPI ID</label>
                <Input
                  value={paymentSettings.upi_id || ''}
                  onChange={(e) => setPaymentSettings({ ...paymentSettings, upi_id: e.target.value })}
                  placeholder="yourname@upi"
                  className="bg-[#0f1f35] border-[#d4a84b]/30 text-white"
                />
              </div>
              
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="text-sm text-[#71717a] mb-2 block">Bank Name</label>
                  <Input
                    value={paymentSettings.bank_name || ''}
                    onChange={(e) => setPaymentSettings({ ...paymentSettings, bank_name: e.target.value })}
                    className="bg-[#0f1f35] border-[#d4a84b]/30 text-white"
                  />
                </div>
                <div>
                  <label className="text-sm text-[#71717a] mb-2 block">Account Number</label>
                  <Input
                    value={paymentSettings.account_number || ''}
                    onChange={(e) => setPaymentSettings({ ...paymentSettings, account_number: e.target.value })}
                    className="bg-[#0f1f35] border-[#d4a84b]/30 text-white"
                  />
                </div>
                <div>
                  <label className="text-sm text-[#71717a] mb-2 block">IFSC Code</label>
                  <Input
                    value={paymentSettings.ifsc_code || ''}
                    onChange={(e) => setPaymentSettings({ ...paymentSettings, ifsc_code: e.target.value })}
                    className="bg-[#0f1f35] border-[#d4a84b]/30 text-white"
                  />
                </div>
                <div>
                  <label className="text-sm text-[#71717a] mb-2 block">Account Holder</label>
                  <Input
                    value={paymentSettings.account_holder || ''}
                    onChange={(e) => setPaymentSettings({ ...paymentSettings, account_holder: e.target.value })}
                    className="bg-[#0f1f35] border-[#d4a84b]/30 text-white"
                  />
                </div>
              </div>
              
              <Button
                onClick={updatePaymentSettings}
                className="bg-[#d4a84b] text-[#0a1628] hover:bg-[#c49a40]"
                disabled={loading}
              >
                Save Payment Settings
              </Button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

// Main Alert Dashboard (for verified users)
const AlertDashboard = () => {
  const { user, token, logout } = useAuth();
  const [buyAlerts, setBuyAlerts] = useState([]);
  const [sellAlerts, setSellAlerts] = useState([]);
  const [adminMessages, setAdminMessages] = useState([]);
  const [pastNotifications, setPastNotifications] = useState([]);
  const [expandedItems, setExpandedItems] = useState({});
  const [isConnected, setIsConnected] = useState(false);
  const [lastRefresh, setLastRefresh] = useState(Date.now());
  const [welcomeMessage, setWelcomeMessage] = useState('');
  const [showWelcome, setShowWelcome] = useState(false);
  const [showStrategyPage, setShowStrategyPage] = useState(false);
  const [deferredPrompt, setDeferredPrompt] = useState(null);
  const [showInstallBtn, setShowInstallBtn] = useState(true);
  const [showIosHelp, setShowIosHelp] = useState(false);
  const [pastFilter, setPastFilter] = useState('all');
  const [strategies, setStrategies] = useState([]);
  const [trialStrategyIds, setTrialStrategyIds] = useState([]);
  const [isTrial, setIsTrial] = useState(false);
  const [isRestricted, setIsRestricted] = useState(false);
  const [allowedStrategyIds, setAllowedStrategyIds] = useState([]);
  const wsRef = useRef(null);
  const audioRef = useRef(null);
  const soundIntervalRef = useRef(null);
  const soundTimeoutRef = useRef(null);
  const pingIntervalRef = useRef(null);

  // ---- Pair / strategy rendering helpers ----
  const getStrategyName = (sid) => {
    if (!sid) return null;
    return strategies.find(s => s.id === sid)?.name || null;
  };

  const renderPairInline = (buyStock, sellStock, sellFirst = false) => {
    const buyPart = (
      <span className="font-mono font-bold text-white">
        {buyStock} <span className="text-green-400">(Buy)</span>
      </span>
    );
    const sellPart = (
      <span className="font-mono font-bold text-white">
        {sellStock} <span className="text-red-400">(Sell)</span>
      </span>
    );
    return (
      <span className="font-mono text-sm">
        {sellFirst ? sellPart : buyPart}
        <span className="text-fuchsia-400 mx-1.5">⇄</span>
        {sellFirst ? buyPart : sellPart}
      </span>
    );
  };
  const pollingIntervalRef = useRef(null);
  const reconnectTimeoutRef = useRef(null);
  const reconnectAttempts = useRef(0);
  const isComponentMounted = useRef(true);

  // PWA Install prompt handler
  useEffect(() => {
    const handler = (e) => {
      e.preventDefault();
      setDeferredPrompt(e);
    };
    window.addEventListener('beforeinstallprompt', handler);
    return () => window.removeEventListener('beforeinstallprompt', handler);
  }, []);

  const handleInstallClick = async () => {
    if (deferredPrompt) {
      deferredPrompt.prompt();
      const { outcome } = await deferredPrompt.userChoice;
      if (outcome === 'accepted') {
        toast.success('App installed successfully!');
      }
      setDeferredPrompt(null);
    } else {
      // Fallback: show manual install instructions
      const isIOS = /iPad|iPhone|iPod/.test(navigator.userAgent);
      if (isIOS) {
        toast.info('Tap the Share button, then "Add to Home Screen"', { duration: 5000 });
      } else {
        toast.info('Tap your browser menu (3 dots) and select "Install App" or "Add to Home Screen"', { duration: 5000 });
      }
    }
  };

  // Request notification permission on mount
  useEffect(() => {
    if ('Notification' in window && Notification.permission === 'default') {
      Notification.requestPermission();
    }
  }, []);

  // Fetch welcome message on mount
  useEffect(() => {
    const fetchWelcome = async () => {
      try {
        const res = await axios.get(`${API}/settings/welcome-message`, {
          headers: { Authorization: `Bearer ${token}` }
        });
        if (res.data.welcome_message) {
          setWelcomeMessage(res.data.welcome_message);
          // Show welcome only once per session
          const lastShown = sessionStorage.getItem('welcomeShown');
          if (!lastShown) {
            setShowWelcome(true);
            sessionStorage.setItem('welcomeShown', 'true');
          }
        }
      } catch (e) {
      }
    };
    fetchWelcome();
  }, [token]);

  // Show browser notification (works in background)
  const showBrowserNotification = useCallback((title, body, tag) => {
    if ('Notification' in window && Notification.permission === 'granted') {
      try {
        const notification = new Notification(title, {
          body: body,
          icon: '/logo.png',
          badge: '/logo.png',
          tag: tag || 'wolffs-alert',
          requireInteraction: true,
          vibrate: [200, 100, 200, 100, 200]
        });
        
        notification.onclick = () => {
          window.focus();
          notification.close();
        };
      } catch (e) {
        console.error('Failed to show browser notification:', e);
      }
    }
  }, []);

  // Sound management - 5 sec ring, 30 sec pause, repeat with prominent bell
  const startAlertSound = useCallback(() => {
    if (soundIntervalRef.current) clearInterval(soundIntervalRef.current);
    if (soundTimeoutRef.current) clearTimeout(soundTimeoutRef.current);
    
    const playBellSound = () => {
      if (audioRef.current) {
        // Reset and play
        audioRef.current.currentTime = 0;
        audioRef.current.volume = 1.0; // Full volume
        audioRef.current.play().catch((err) => {
        });
        
        // Stop after 5 seconds
        soundTimeoutRef.current = setTimeout(() => {
          if (audioRef.current) {
            audioRef.current.pause();
            audioRef.current.currentTime = 0;
          }
        }, 5000);
      }
    };
    
    // Play immediately
    playBellSound();
    
    // Repeat every 35 seconds (5 sec sound + 30 sec pause)
    soundIntervalRef.current = setInterval(playBellSound, 35000);
  }, []);

  const stopAlertSound = useCallback(() => {
    if (soundIntervalRef.current) {
      clearInterval(soundIntervalRef.current);
      soundIntervalRef.current = null;
    }
    if (soundTimeoutRef.current) {
      clearTimeout(soundTimeoutRef.current);
      soundTimeoutRef.current = null;
    }
    if (audioRef.current) {
      audioRef.current.pause();
      audioRef.current.currentTime = 0;
    }
  }, []);

  // Fetch alerts with error handling
  const fetchAlerts = useCallback(async () => {
    if (!isComponentMounted.current) return;
    
    try {
      const [activeRes, pastRes, stratRes] = await Promise.all([
        axios.get(`${API}/alerts/active`, { headers: { Authorization: `Bearer ${token}` } }),
        axios.get(`${API}/alerts/past`, { headers: { Authorization: `Bearer ${token}` } }),
        axios.get(`${API}/strategies`, { headers: { Authorization: `Bearer ${token}` } })
      ]);
      
      if (!isComponentMounted.current) return;
      
      setBuyAlerts(activeRes.data.buy_alerts || []);
      setSellAlerts(activeRes.data.sell_alerts || []);
      setAdminMessages(activeRes.data.admin_messages || []);
      setPastNotifications(pastRes.data.past_notifications || []);
      setStrategies(stratRes.data.strategies || []);
      setIsTrial(pastRes.data.is_trial || false);
      setTrialStrategyIds(pastRes.data.trial_strategy_ids || []);
      setIsRestricted(pastRes.data.is_restricted || false);
      setAllowedStrategyIds(pastRes.data.allowed_strategy_ids || []);
      setLastRefresh(Date.now());
    } catch (e) {
      if (e.response?.status === HTTP_UNAUTHORIZED) {
        logout();
        toast.error('Session expired. Please login again.');
      }
    }
  }, [token, logout]);

  // WebSocket connection with exponential backoff
  const connectWebSocket = useCallback(() => {
    if (!isComponentMounted.current) return;
    
    if (wsRef.current) {
      wsRef.current.close();
      wsRef.current = null;
    }
    
    if (pingIntervalRef.current) {
      clearInterval(pingIntervalRef.current);
      pingIntervalRef.current = null;
    }

    try {
      const ws = new WebSocket(`${WS_URL}?token=${token}`);
      
      ws.onopen = () => {
        if (!isComponentMounted.current) return;
        setIsConnected(true);
        reconnectAttempts.current = 0;
        
        pingIntervalRef.current = setInterval(() => {
          if (ws.readyState === WebSocket.OPEN) {
            ws.send("ping");
          }
        }, WS_PING_INTERVAL_MS);
      };
      
      ws.onclose = (event) => {
        if (!isComponentMounted.current) return;
        setIsConnected(false);
        
        if (pingIntervalRef.current) {
          clearInterval(pingIntervalRef.current);
          pingIntervalRef.current = null;
        }
        
        const delay = Math.min(1000 * Math.pow(2, reconnectAttempts.current), WS_MAX_RECONNECT_DELAY_MS);
        reconnectAttempts.current++;
        
        reconnectTimeoutRef.current = setTimeout(connectWebSocket, delay);
      };
      
      ws.onerror = (error) => {
      };
      
      ws.onmessage = (event) => {
        if (!isComponentMounted.current) return;
        
        try {
          if (event.data === "pong") return;
          
          const data = JSON.parse(event.data);
          
          if (data.type === "new_alert" || data.type === "admin_message" || data.type === "strategy_reset") {
            fetchAlerts();
          }
        } catch (e) {
          console.error('Failed to parse WebSocket message:', e);
        }
      };
      
      wsRef.current = ws;
    } catch (error) {
      setIsConnected(false);
      
      const delay = Math.min(1000 * Math.pow(2, reconnectAttempts.current), WS_MAX_RECONNECT_DELAY_MS);
      reconnectAttempts.current++;
      reconnectTimeoutRef.current = setTimeout(connectWebSocket, delay);
    }
  }, [token, fetchAlerts]);

  // Auto-refresh polling as backup (every 30 seconds)
  useEffect(() => {
    fetchAlerts();
    
    pollingIntervalRef.current = setInterval(() => {
      fetchAlerts();
    }, POLLING_INTERVAL_MS);
    
    return () => {
      if (pollingIntervalRef.current) {
        clearInterval(pollingIntervalRef.current);
      }
    };
  }, [fetchAlerts]);

  // WebSocket connection management
  useEffect(() => {
    isComponentMounted.current = true;
    connectWebSocket();
    
    const handleVisibilityChange = () => {
      if (document.visibilityState === 'visible') {
        fetchAlerts();
        
        if (!wsRef.current || wsRef.current.readyState !== WebSocket.OPEN) {
          reconnectAttempts.current = 0;
          connectWebSocket();
        }
      }
    };
    
    document.addEventListener('visibilitychange', handleVisibilityChange);
    
    const handleOnline = () => {
      reconnectAttempts.current = 0;
      connectWebSocket();
      fetchAlerts();
    };
    
    window.addEventListener('online', handleOnline);
    
    return () => {
      isComponentMounted.current = false;
      
      document.removeEventListener('visibilitychange', handleVisibilityChange);
      window.removeEventListener('online', handleOnline);
      
      if (wsRef.current) {
        wsRef.current.close();
        wsRef.current = null;
      }
      if (pingIntervalRef.current) {
        clearInterval(pingIntervalRef.current);
      }
      if (reconnectTimeoutRef.current) {
        clearTimeout(reconnectTimeoutRef.current);
      }
    };
  }, [connectWebSocket, fetchAlerts]);

  // Audio setup with prominent bell sound
  useEffect(() => {
    audioRef.current = new Audio("/bell-sound.mp3");
    audioRef.current.loop = false;
    audioRef.current.volume = 1.0;
    audioRef.current.preload = 'auto';
    audioRef.current.load();
    
    return () => {
      stopAlertSound();
    };
  }, [stopAlertSound]);

  // Start/stop sound and show notifications based on active alerts
  useEffect(() => {
    const hasAlerts = buyAlerts.length > 0 || sellAlerts.length > 0 || adminMessages.length > 0;
    
    if (hasAlerts) {
      startAlertSound();
      
      // Show browser notification for new alerts (works in background)
      const totalBuy = buyAlerts.length;
      const totalSell = sellAlerts.length;
      const totalAdmin = adminMessages.length;
      
      if (totalBuy > 0) {
        const symbols = buyAlerts.slice(0, 3).map(a => a.stock_symbol).join(', ');
        showBrowserNotification(
          `🟢 ${totalBuy} BUY Alert${totalBuy > 1 ? 's' : ''}`,
          `${symbols}${totalBuy > 3 ? ` +${totalBuy - 3} more` : ''}`,
          'buy-alert'
        );
      }
      
      if (totalSell > 0) {
        const symbols = sellAlerts.slice(0, 3).map(a => a.stock_symbol).join(', ');
        showBrowserNotification(
          `🔴 ${totalSell} SELL Alert${totalSell > 1 ? 's' : ''}`,
          `${symbols}${totalSell > 3 ? ` +${totalSell - 3} more` : ''}`,
          'sell-alert'
        );
      }
      
      if (totalAdmin > 0) {
        showBrowserNotification(
          '📢 Admin Message',
          adminMessages[0].message_content?.substring(0, 100) || 'New message from admin',
          'admin-message'
        );
      }
    } else {
      stopAlertSound();
    }
  }, [buyAlerts.length, sellAlerts.length, adminMessages.length, startAlertSound, stopAlertSound, showBrowserNotification, buyAlerts, sellAlerts, adminMessages]);

  const handleManualRefresh = () => {
    fetchAlerts();
    toast.success('Refreshed!');
  };

  const acknowledgeAlert = async (alertId) => {
    // Optimistic UI: remove instantly from all alert lists
    setBuyAlerts(prev => prev.filter(a => a.id !== alertId));
    setSellAlerts(prev => prev.filter(a => a.id !== alertId));
    setAdminMessages(prev => prev.filter(a => a.id !== alertId));
    try {
      await axios.post(`${API}/alerts/${alertId}/acknowledge`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
    } catch (e) {
      toast.error('Failed to acknowledge alert');
      fetchAlerts(); // revert by re-fetching truth
    }
  };

  const acknowledgeAllByType = async (type) => {
    // Optimistic UI: clear list instantly
    const hadAlerts = type === 'buy' ? buyAlerts.length > 0 : sellAlerts.length > 0;
    if (type === 'buy') setBuyAlerts([]);
    else setSellAlerts([]);
    stopAlertSound();
    try {
      await axios.post(
        `${API}/alerts/acknowledge-all?alert_type=${type}`,
        {},
        { headers: { Authorization: `Bearer ${token}` } }
      );
      if (hadAlerts) {
        toast.success(`All ${type.toUpperCase()} alerts acknowledged`);
      }
    } catch (e) {
      toast.error('Failed to acknowledge alerts. Please try again.');
      fetchAlerts(); // revert by re-fetching truth
    }
  };

  const acknowledgeAll = async () => {
    try {
      await axios.post(`${API}/alerts/acknowledge-all`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      fetchAlerts();
      toast.success('All alerts acknowledged');
    } catch (e) {
      toast.error('Failed to acknowledge alerts');
    }
  };

  const toggleExpand = (id) => {
    setExpandedItems(prev => ({ ...prev, [id]: !prev[id] }));
  };

  const formatTime = (timeStr) => {
    if (!timeStr) return "";
    if (timeStr.includes(":") && !timeStr.includes("T")) return timeStr;
    try {
      return new Date(timeStr).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
    } catch {
      return timeStr;
    }
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return "";
    try {
      return new Date(dateStr).toLocaleDateString([], { month: 'short', day: 'numeric' });
    } catch {
      return dateStr;
    }
  };

  // Convert URLs in text to clickable links
  const renderWithLinks = (text) => {
    if (!text) return null;
    const urlRegex = /(https?:\/\/[^\s]+)/g;
    const parts = text.split(urlRegex);
    
    return parts.map((part, index) => {
      if (part.match(urlRegex)) {
        return (
          <a 
            key={`link-${part}-${index}`} 
            href={part} 
            target="_blank" 
            rel="noopener noreferrer"
            className="text-[#d4a84b] underline hover:text-[#f0c060]"
          >
            {part}
          </a>
        );
      }
      return <span key={`text-${index}`}>{part}</span>;
    });
  };

  // Show strategy selection page (via settings)
  if (showStrategyPage) {
    return <StrategySelectionPage onDone={() => setShowStrategyPage(false)} />;
  }

  // Show welcome message popup (only once per session)
  if (showWelcome && welcomeMessage) {
    return (
      <div className="app-container" data-testid="welcome-message-overlay">
        <Toaster position="top-right" theme="dark" />
        <div className="alert-overlay">
          <div className="alert-card-multi alert-enter neon-glow" style={{maxWidth: '400px'}}>
            <div className="alert-header-info">
              <img src="/logo.png" alt="Logo" className="w-16 h-16 mx-auto mb-3 logo-blend" />
              <span className="text-xs text-[#71717a] uppercase tracking-widest">Welcome</span>
              <div className="text-xl font-bold text-[#d4a84b] mt-2">{user?.name || 'Trader'}</div>
            </div>
            
            <div className="admin-message-content text-center" style={{whiteSpace: 'pre-wrap'}}>
              {renderWithLinks(welcomeMessage)}
            </div>
            
            <div className="acknowledge-container">
              <button onClick={() => setShowWelcome(false)} className="acknowledge-btn">
                <Zap className="w-5 h-5 inline mr-2" />
                START TRADING
              </button>
            </div>
          </div>
        </div>
      </div>
    );
  }

  // Show admin message popup first
  if (adminMessages.length > 0) {
    const msg = adminMessages[0];
    if (msg.is_pair) {
      const phaseLabel = msg.is_exit ? 'PAIR EXIT' : (msg.pair_avg_count > 0 ? `PAIR AVG ${msg.pair_avg_count}` : 'PAIR TRADE');
      const stratName = getStrategyName(msg.strategy_id);
      return (
        <div className="app-container" data-testid="pair-popup-overlay">
          <Toaster position="top-right" theme="dark" />
          <div className="alert-overlay">
            <div className="alert-card-multi alert-enter alert-pulse neon-glow">
              <div className="alert-header-info">
                <div className="text-xs text-[#d4a84b] uppercase tracking-widest font-bold mb-1">⚡ IMP NOTIFICATION</div>
                {stratName ? <div className="alert-condition-name" data-testid="pair-strategy-name">{stratName}</div> : null}
                <div className="flex items-center justify-center gap-2 my-2">
                  <span className="text-xs font-bold px-2 py-0.5 rounded bg-fuchsia-500/30 text-fuchsia-400 ring-1 ring-fuchsia-500/40">⇆ {phaseLabel}</span>
                  {msg.is_exit ? (
                    <span className="text-xs font-bold px-2 py-0.5 rounded bg-purple-500/30 text-purple-400">EXIT</span>
                  ) : null}
                </div>
                <div className="alert-time-display">{formatTime(msg.triggered_at)}</div>
              </div>

              <div className="admin-message-content" style={{textAlign: 'center'}}>
                <div className="text-xl py-4" data-testid="pair-popup-body">
                  {renderPairInline(msg.pair_buy_stock, msg.pair_sell_stock, false)}
                </div>
                {msg.condition_name ? <p className="text-xs text-[#71717a] mt-3">{msg.condition_name}</p> : null}
              </div>

              <div className="acknowledge-container">
                <button onClick={() => acknowledgeAlert(msg.id)} className="acknowledge-btn" data-testid="pair-ack-btn">
                  <Volume2 className="w-5 h-5 inline mr-2" />
                  ACKNOWLEDGE
                </button>
              </div>
            </div>
          </div>
        </div>
      );
    }
    if (msg.is_news) {
      const bullish = msg.news_side === 'buy';
      const summaryLines = Array.isArray(msg.news_summary) ? msg.news_summary : [];
      const stratName = getStrategyName(msg.strategy_id);
      return (
        <div className="app-container" data-testid="news-popup-overlay">
          <Toaster position="top-right" theme="dark" />
          <div className="alert-overlay">
            <div className={`alert-card-multi alert-enter alert-pulse neon-glow ${bullish ? 'news-bullish' : 'news-bearish'}`}>
              <div className="alert-header-info">
                <div className="text-xs text-[#d4a84b] uppercase tracking-widest font-bold mb-1">⚡ IMP NOTIFICATION</div>
                {stratName ? <div className="alert-condition-name" data-testid="news-strategy-name">{stratName}</div> : null}
                <div className="flex items-center justify-center gap-2 my-2">
                  <span className="text-xs font-bold px-2 py-0.5 rounded bg-blue-500/30 text-blue-400 ring-1 ring-blue-500/40">📰 NEWS</span>
                  <span className={`text-xs font-bold px-2 py-0.5 rounded ${bullish ? 'bg-green-500/30 text-green-400' : 'bg-red-500/30 text-red-400'}`}>
                    {bullish ? 'BULLISH' : 'BEARISH'}
                  </span>
                </div>
                <div className="text-lg font-mono font-bold text-white" data-testid="news-symbol">{msg.stock_symbol}</div>
                <div className="alert-time-display">
                  {msg.news_date ? `${msg.news_date} • ` : ''}{formatTime(msg.triggered_at)}
                </div>
              </div>

              <div className="admin-message-content" style={{textAlign: 'left'}}>
                {msg.news_headline ? (
                  <div className="text-[#d4a84b] font-semibold text-base mb-3" data-testid="news-headline">{msg.news_headline}</div>
                ) : null}
                {summaryLines.length > 0 ? (
                  <ul className="space-y-1.5 text-sm text-[#a1a1aa]">
                    {summaryLines.map((line, i) => (
                      <li key={i} className="flex gap-2"><span className={bullish ? 'text-green-400' : 'text-red-400'}>•</span><span>{line}</span></li>
                    ))}
                  </ul>
                ) : (msg.message_content ? <p className="text-sm text-[#a1a1aa]">{msg.message_content}</p> : null)}
                {msg.news_source ? (
                  <p className="text-xs text-[#71717a] mt-3 text-right italic">— {msg.news_source}</p>
                ) : null}
              </div>

              <div className="acknowledge-container">
                <button onClick={() => acknowledgeAlert(msg.id)} className="acknowledge-btn" data-testid="news-ack-btn">
                  <Volume2 className="w-5 h-5 inline mr-2" />
                  ACKNOWLEDGE
                </button>
              </div>
            </div>
          </div>
        </div>
      );
    }
    return (
      <div className="app-container" data-testid="admin-message-overlay">
        <Toaster position="top-right" theme="dark" />
        <div className="alert-overlay">
          <div className="alert-card-multi alert-enter alert-pulse neon-glow">
            <div className="alert-header-info">
              <Shield className="w-12 h-12 text-[#d4a84b] mx-auto mb-2" />
              <div className="text-xs text-[#d4a84b] uppercase tracking-widest font-bold mb-1">⚡ IMP NOTIFICATION</div>
              <div className="alert-condition-name" data-testid="admin-msg-strategy-name">
                {getStrategyName(msg.strategy_id) || (msg.is_private ? 'Private Message' : 'Admin Notification')}
              </div>
              <div className="alert-time-display">{formatTime(msg.triggered_at)}</div>
            </div>
            
            <div className="admin-message-content">
              {msg.message_content}
            </div>
            
            <div className="acknowledge-container">
              <button onClick={() => acknowledgeAlert(msg.id)} className="acknowledge-btn">
                <Volume2 className="w-5 h-5 inline mr-2" />
                ACKNOWLEDGE
              </button>
            </div>
          </div>
        </div>
      </div>
    );
  }

  const hasActiveAlerts = buyAlerts.length > 0 || sellAlerts.length > 0;

  return (
    <div className="app-container" data-testid="alert-dashboard">
      <Toaster position="top-right" theme="dark" />
      
      {/* Header */}
      <header className="app-header">
        <div className="flex items-center justify-between px-3 sm:px-6 py-3 max-w-[800px] mx-auto w-full gap-2">
          <div className="flex items-center gap-2 sm:gap-3 min-w-0 flex-1">
            <img
              src="/logo.png"
              alt="Logo"
              className="w-9 h-9 sm:w-10 sm:h-10 rounded-lg shrink-0 logo-blend"
            />
            <div className="min-w-0 flex-1">
              <h1 className="font-mono text-[13px] sm:text-base font-bold text-[#d4a84b] truncate">WOLFFS INSTAALERTS</h1>
              <p className="text-[11px] sm:text-xs text-[#71717a] truncate">{user?.name || user?.mobile || user?.email}</p>
              {user?.plan_end_date && (
                <p className="text-[10px] sm:text-xs text-[#d4a84b]/70 truncate">
                  Expires: {new Date(user.plan_end_date).toLocaleDateString()}
                </p>
              )}
            </div>
          </div>
          
          <div className="flex items-center gap-1 shrink-0">
            {showInstallBtn && (
              <Button 
                onClick={handleInstallClick}
                variant="outline"
                size="sm"
                className="text-[#d4a84b] border-[#d4a84b]/50 hover:bg-[#d4a84b]/20 text-[11px] px-2 h-8"
                data-testid="install-btn"
                title="Install App"
              >
                <Smartphone className="w-4 h-4 sm:mr-1" />
                <span className="hidden sm:inline">Install</span>
              </Button>
            )}

            <Button
              onClick={() => setShowIosHelp(true)}
              variant="ghost"
              size="sm"
              className="text-[#71717a] hover:text-[#d4a84b] p-1.5 sm:hidden"
              title="iPhone Install Guide"
              data-testid="ios-help-btn"
            >
              <span className="text-[11px]">iOS?</span>
            </Button>
            
            <Button 
              onClick={() => setShowStrategyPage(true)} 
              variant="ghost" 
              size="sm" 
              className="text-[#71717a] hover:text-[#d4a84b] p-1.5 sm:p-2"
              data-testid="settings-btn"
            >
              <Settings className="w-4 h-4 sm:w-5 sm:h-5" />
            </Button>
            
            <Button 
              onClick={handleManualRefresh} 
              variant="ghost" 
              size="sm" 
              className="text-[#71717a] hover:text-[#d4a84b] p-1.5 sm:p-2"
              data-testid="refresh-btn"
            >
              <svg className="w-4 h-4 sm:w-5 sm:h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
              </svg>
            </Button>
            
            <div className={`flex items-center gap-1 px-1.5 sm:px-2 py-1 rounded-full text-xs ${isConnected ? 'bg-green-500/20 text-green-400' : 'bg-red-500/20 text-red-400'}`}>
              {isConnected ? <Wifi className="w-3 h-3" /> : <WifiOff className="w-3 h-3" />}
              <span className={`w-1.5 h-1.5 rounded-full ${isConnected ? 'bg-green-400 animate-pulse' : 'bg-red-400'}`} />
            </div>
            
            <Button onClick={logout} variant="ghost" size="sm" className="text-[#71717a] hover:text-white p-2">
              <LogOut className="w-5 h-5" />
            </Button>
          </div>
        </div>
      </header>

      {/* Main Content - Vertical 3-Section Layout */}
      <main className="flex-1 overflow-y-auto px-4 pb-4 max-w-[800px] mx-auto w-full" style={{paddingTop: '100px'}}>
        <div className="space-y-4">
          
          {/* BUY ALERTS Section */}
          <section className="alert-section buy-section" data-testid="buy-alerts-section">
            <div className="section-header buy-header">
              <div className="flex items-center gap-2">
                <div className="w-3 h-3 rounded-full bg-green-500" />
                <h2 className="font-mono font-bold text-green-400">BUY ALERTS</h2>
                <span className="text-xs bg-green-500/20 text-green-400 px-2 py-0.5 rounded-full">
                  {buyAlerts.length}
                </span>
              </div>
            </div>
            
            <div className="section-content">
              {buyAlerts.length === 0 ? (
                <div className="empty-section py-6">
                  <div className="w-12 h-12 rounded-full bg-green-500/10 flex items-center justify-center mx-auto mb-2">
                    <Bell className="w-6 h-6 text-green-500/40" />
                  </div>
                  <p className="text-[#71717a] text-sm">Waiting for BUY alerts...</p>
                </div>
              ) : (
                <>
                  <div className="alerts-list-scrollable">
                    {buyAlerts.map((alert) => {
                      const isPair = alert.is_pair === true;
                      const avgMatch = isPair ? null : parseAvgSymbol(alert.stock_symbol);
                      const exitBase = !isPair && alert.is_exit ? parseExitSymbol(alert.stock_symbol) : null;
                      const displaySymbol = isPair ? null : (exitBase || (avgMatch ? avgMatch.base : alert.stock_symbol));
                      return (
                      <div key={alert.id} className={`alert-item buy-item ${alert.is_exit ? 'exit-alert' : ''}`}>
                        <div className="flex items-center gap-3 flex-wrap">
                          {isPair ? (
                            <>
                              <span className="text-xs font-bold px-2 py-0.5 rounded bg-fuchsia-500/30 text-fuchsia-400 shrink-0 ring-1 ring-fuchsia-500/40">⇆ PAIR</span>
                              {alert.is_exit ? (
                                <span className="text-xs font-bold px-2 py-0.5 rounded bg-purple-500/30 text-purple-400 shrink-0">EXIT</span>
                              ) : (alert.pair_avg_count > 0 ? (
                                <span className="text-xs font-bold px-2 py-0.5 rounded bg-orange-500/30 text-orange-400 shrink-0 ring-1 ring-orange-500/40">AVG {alert.pair_avg_count}</span>
                              ) : null)}
                              {renderPairInline(alert.pair_buy_stock, alert.pair_sell_stock, false)}
                            </>
                          ) : (
                          <>
                          {alert.is_exit ? (
                            <span className="text-xs font-bold px-2 py-0.5 rounded bg-purple-500/30 text-purple-400 shrink-0" data-testid="active-exit-badge">EXIT</span>
                          ) : null}
                          {avgMatch && !alert.is_exit ? (
                            <span className="text-xs font-bold px-2 py-0.5 rounded bg-orange-500/30 text-orange-400 shrink-0 ring-1 ring-orange-500/40" data-testid={`avg-badge-${avgMatch.avgN}`}>
                              AVG {avgMatch.avgN}
                            </span>
                          ) : null}
                          <span className="font-mono font-bold text-white text-lg">{displaySymbol}</span>
                          {alert.condition_name ? (
                            <span className="text-xs text-[#71717a] truncate max-w-[100px]">{alert.condition_name}</span>
                          ) : null}
                          </>
                          )}
                        </div>
                        <div className="flex items-center gap-2">
                          <span className="text-sm text-green-400 font-mono">{formatTime(alert.triggered_at)}</span>
                          <button 
                            onClick={() => acknowledgeAlert(alert.id)} 
                            className="p-1.5 rounded hover:bg-green-500/20 text-[#71717a] hover:text-green-400"
                          >
                            <Check className="w-4 h-4" />
                          </button>
                        </div>
                      </div>
                      );
                    })}
                  </div>
                  <button 
                    onClick={() => acknowledgeAllByType('buy')} 
                    className="w-full mt-3 py-2.5 bg-green-500/20 hover:bg-green-500/30 text-green-400 rounded-lg font-bold text-sm transition-colors"
                    data-testid="ack-all-buy-btn"
                  >
                    ACKNOWLEDGE ALL BUY
                  </button>
                </>
              )}
            </div>
          </section>

          {/* SELL ALERTS Section */}
          <section className="alert-section sell-section" data-testid="sell-alerts-section">
            <div className="section-header sell-header">
              <div className="flex items-center gap-2">
                <div className="w-3 h-3 rounded-full bg-red-500" />
                <h2 className="font-mono font-bold text-red-400">SELL ALERTS</h2>
                <span className="text-xs bg-red-500/20 text-red-400 px-2 py-0.5 rounded-full">
                  {sellAlerts.length}
                </span>
              </div>
            </div>
            
            <div className="section-content">
              {sellAlerts.length === 0 ? (
                <div className="empty-section py-6">
                  <div className="w-12 h-12 rounded-full bg-red-500/10 flex items-center justify-center mx-auto mb-2">
                    <Bell className="w-6 h-6 text-red-500/40" />
                  </div>
                  <p className="text-[#71717a] text-sm">Waiting for SELL alerts...</p>
                </div>
              ) : (
                <>
                  <div className="alerts-list-scrollable">
                    {sellAlerts.map((alert) => {
                      const isPair = alert.is_pair === true;
                      return (
                      <div key={alert.id} className={`alert-item sell-item ${alert.is_exit ? 'exit-alert' : ''}`}>
                        <div className="flex items-center gap-3 flex-wrap">
                          {isPair ? (
                            <>
                              <span className="text-xs font-bold px-2 py-0.5 rounded bg-fuchsia-500/30 text-fuchsia-400 shrink-0 ring-1 ring-fuchsia-500/40">⇆ PAIR</span>
                              {alert.is_exit ? (
                                <span className="text-xs font-bold px-2 py-0.5 rounded bg-purple-500/30 text-purple-400 shrink-0">EXIT</span>
                              ) : (alert.pair_avg_count > 0 ? (
                                <span className="text-xs font-bold px-2 py-0.5 rounded bg-orange-500/30 text-orange-400 shrink-0 ring-1 ring-orange-500/40">AVG {alert.pair_avg_count}</span>
                              ) : null)}
                              {renderPairInline(alert.pair_buy_stock, alert.pair_sell_stock, true)}
                            </>
                          ) : (
                          <>
                          {alert.is_exit ? (
                            <span className="text-xs font-bold px-2 py-0.5 rounded bg-purple-500/30 text-purple-400 shrink-0">EXIT</span>
                          ) : null}
                          <span className="font-mono font-bold text-white text-lg">{alert.stock_symbol}</span>
                          {alert.condition_name ? (
                            <span className="text-xs text-[#71717a] truncate max-w-[100px]">{alert.condition_name}</span>
                          ) : null}
                          </>
                          )}
                        </div>
                        <div className="flex items-center gap-2">
                          <span className="text-sm text-red-400 font-mono">{formatTime(alert.triggered_at)}</span>
                          <button 
                            onClick={() => acknowledgeAlert(alert.id)} 
                            className="p-1.5 rounded hover:bg-red-500/20 text-[#71717a] hover:text-red-400"
                          >
                            <Check className="w-4 h-4" />
                          </button>
                        </div>
                      </div>
                      );
                    })}
                  </div>
                  <button 
                    onClick={() => acknowledgeAllByType('sell')} 
                    className="w-full mt-3 py-2.5 bg-red-500/20 hover:bg-red-500/30 text-red-400 rounded-lg font-bold text-sm transition-colors"
                    data-testid="ack-all-sell-btn"
                  >
                    ACKNOWLEDGE ALL SELL ({sellAlerts.length})
                  </button>
                </>
              )}
            </div>
          </section>

          {/* PAST NOTIFICATIONS Section - Full scrollable list */}
          <section className="alert-section past-section" data-testid="past-notifications-section">
            <div className="section-header past-header">
              <div className="flex items-center gap-2">
                <History className="w-4 h-4 text-[#71717a]" />
                <h2 className="font-mono font-bold text-[#71717a] text-sm">PAST NOTIFICATIONS (30 Days)</h2>
                <span className="text-xs bg-[#71717a]/20 text-[#71717a] px-2 py-0.5 rounded-full">
                  {(() => {
                    const userSel = new Set(user?.selected_strategies || []);
                    if (pastFilter === 'all') {
                      return userSel.size > 0
                        ? pastNotifications.filter(n => !n.strategy_id || userSel.has(n.strategy_id)).length
                        : pastNotifications.length;
                    }
                    return pastNotifications.filter(n => n.strategy_id === pastFilter).length;
                  })()}
                </span>
              </div>
            </div>

            {/* Strategy filter pills */}
            <div className="flex gap-1.5 px-3 py-2 overflow-x-auto no-scrollbar" data-testid="past-filter-pills">
              <button
                onClick={() => setPastFilter('all')}
                className={`shrink-0 text-xs font-semibold px-3 py-1.5 rounded-full transition-all ${
                  pastFilter === 'all'
                    ? 'bg-[#d4a84b] text-[#0a1628]'
                    : 'bg-[#1e293b] text-[#71717a] hover:text-white'
                }`}
                data-testid="past-filter-all"
              >
                All
              </button>
              {strategies.map((strat) => {
                const isLockedStrat = isRestricted && !allowedStrategyIds.includes(strat.id);
                return (
                <button
                  key={strat.id}
                  onClick={() => setPastFilter(strat.id)}
                  className={`shrink-0 text-xs font-semibold px-3 py-1.5 rounded-full transition-all flex items-center gap-1 ${
                    pastFilter === strat.id
                      ? isLockedStrat ? 'bg-[#71717a]/30 text-[#71717a]' : 'bg-[#d4a84b] text-[#0a1628]'
                      : 'bg-[#1e293b] text-[#71717a] hover:text-white'
                  }`}
                  data-testid={`past-filter-${strat.id}`}
                >
                  {isLockedStrat && <Lock className="w-3 h-3" />}
                  {strat.name}
                </button>
                );
              })}
            </div>
            
            <div className="section-content">
              {(() => {
                const isLockedFilter = isRestricted && pastFilter !== 'all' && !allowedStrategyIds.includes(pastFilter);
                if (isLockedFilter) {
                  return (
                    <div className="py-8 text-center">
                      <Lock className="w-8 h-8 text-[#d4a84b] mx-auto mb-3" />
                      <p className="text-white font-semibold mb-1">Subscribe for Full Access</p>
                      <p className="text-[#71717a] text-sm">Upgrade your plan to view notifications for this strategy</p>
                    </div>
                  );
                }
                const userSelSet = new Set(user?.selected_strategies || []);
                let filtered;
                if (pastFilter === 'all') {
                  // "All" = only strategies the user has selected in settings
                  filtered = userSelSet.size > 0
                    ? pastNotifications.filter(n => !n.strategy_id || userSelSet.has(n.strategy_id))
                    : pastNotifications;
                } else {
                  // Specific filter: show that strategy's past even if not selected (preview)
                  filtered = pastNotifications.filter(n => n.strategy_id === pastFilter);
                }
                return filtered.length === 0 ? (
                  <div className="py-4 text-center">
                    <p className="text-[#71717a] text-sm">No past notifications{pastFilter !== 'all' ? ' for this strategy' : ' for your selected strategies'}</p>
                  </div>
                ) : (
                <div className="past-list-scrollable">
                  {filtered.map((item) => {
                    const isPair = item.is_pair === true;
                    const isNews = item.is_news === true;
                    const summaryLines = isNews && Array.isArray(item.news_summary) ? item.news_summary : [];
                    const pastAvg = item.is_admin_message || isNews || isPair ? null : parseAvgSymbol(item.stock_symbol);
                    const pastExitBase = item.is_exit && !item.is_admin_message ? parseExitSymbol(item.stock_symbol) : null;
                    const pastDisplay = isNews
                      ? item.stock_symbol
                      : item.is_admin_message
                        ? (item.strategy_id
                            ? (strategies.find(s => s.id === item.strategy_id)?.name || 'Strategy Message')
                            : 'Admin Message')
                        : (pastExitBase || (pastAvg ? pastAvg.base : item.stock_symbol));
                    return (
                    <div key={item.id} className="past-item">
                      <button 
                        onClick={() => toggleExpand(item.id)}
                        className="w-full flex items-center justify-between py-2.5 px-2 hover:bg-white/5 rounded transition-colors"
                      >
                        <div className="flex items-center gap-2 flex-wrap">
                          {isPair ? (
                            <>
                              <span className="text-xs font-bold px-2 py-0.5 rounded bg-fuchsia-500/30 text-fuchsia-400 ring-1 ring-fuchsia-500/40">⇆ PAIR</span>
                              {item.is_exit ? (
                                <span className="text-xs font-bold px-2 py-0.5 rounded bg-purple-500/30 text-purple-400">EXIT</span>
                              ) : (item.pair_avg_count > 0 ? (
                                <span className="text-[10px] font-bold px-1.5 py-0.5 rounded bg-orange-500/30 text-orange-400 ring-1 ring-orange-500/40">AVG {item.pair_avg_count}</span>
                              ) : null)}
                              {renderPairInline(item.pair_buy_stock, item.pair_sell_stock, false)}
                            </>
                          ) : isNews ? (
                            <>
                              <span className="text-xs font-bold px-2 py-0.5 rounded bg-blue-500/30 text-blue-400 ring-1 ring-blue-500/40">📰 NEWS</span>
                              <span className={`text-xs font-bold px-2 py-0.5 rounded ${item.news_side === 'sell' ? 'bg-red-500/20 text-red-400' : 'bg-green-500/20 text-green-400'}`}>
                                {item.news_side === 'sell' ? 'BEARISH' : 'BULLISH'}
                              </span>
                              <span className="font-mono text-white text-sm">{pastDisplay}</span>
                            </>
                          ) : (
                            <>
                            <span className={`text-xs font-bold px-2 py-0.5 rounded ${
                              item.is_admin_message ? 'bg-[#d4a84b]/20 text-[#d4a84b]' :
                              item.alert_type === 'sell' ? 'bg-red-500/20 text-red-400' : 'bg-green-500/20 text-green-400'
                            }`}>
                              {item.is_admin_message ? 'MSG' : item.alert_type === 'sell' ? 'SELL' : 'BUY'}
                            </span>
                            <span className="font-mono text-white text-sm">
                              {pastDisplay}
                            </span>
                            {pastAvg && !item.is_exit ? (
                              <span className="text-[10px] font-bold px-1.5 py-0.5 rounded bg-orange-500/30 text-orange-400 ring-1 ring-orange-500/40" data-testid={`past-avg-badge-${item.id}`}>
                                AVG {pastAvg.avgN}
                              </span>
                            ) : null}
                            {item.is_exit && !item.is_admin_message ? (
                              <span className="text-[10px] font-bold px-1.5 py-0.5 rounded bg-purple-500/30 text-purple-400" data-testid={`past-exit-badge-${item.id}`}>
                                EXIT
                              </span>
                            ) : null}
                            </>
                          )}
                        </div>
                        <div className="flex items-center gap-2 text-xs text-[#71717a]">
                          <span>{formatDate(item.created_at)} {formatTime(item.triggered_at)}</span>
                          <svg className={`w-4 h-4 transition-transform ${expandedItems[item.id] ? 'rotate-180' : ''}`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                          </svg>
                        </div>
                      </button>
                      
                      {expandedItems[item.id] && (
                        <div className="px-3 pb-3 text-sm text-[#a1a1aa] bg-white/5 rounded mx-2 mb-2" data-testid={`past-expanded-${item.id}`}>
                          {isNews ? (
                            <div>
                              {item.news_headline ? (
                                <div className="text-[#d4a84b] font-semibold mb-2">{item.news_headline}</div>
                              ) : null}
                              {summaryLines.length > 0 ? (
                                <ul className="space-y-1">
                                  {summaryLines.map((line, i) => (
                                    <li key={i} className="flex gap-2"><span className={item.news_side === 'sell' ? 'text-red-400' : 'text-green-400'}>•</span><span>{line}</span></li>
                                  ))}
                                </ul>
                              ) : (item.message_content ? <p>{item.message_content}</p> : null)}
                              {item.news_source ? (
                                <p className="text-xs text-[#71717a] mt-2 italic">— {item.news_source}</p>
                              ) : null}
                            </div>
                          ) : item.is_admin_message ? (
                            <p className="whitespace-pre-wrap">{item.message_content}</p>
                          ) : (
                            <p><span className="text-[#71717a]">Condition:</span> {item.condition_name || 'N/A'}</p>
                          )}
                        </div>
                      )}
                    </div>
                    );
                  })}
                </div>
              );
              })()}
            </div>
          </section>

        </div>
      </main>
      
      <audio id="alert-audio" preload="auto"><source src="/bell-sound.mp3" type="audio/mpeg" /></audio>

      {/* iOS Install Help Modal */}
      {showIosHelp && (
        <div
          className="fixed inset-0 z-50 bg-black/70 flex items-center justify-center p-4"
          onClick={() => setShowIosHelp(false)}
          data-testid="ios-help-modal"
        >
          <div
            className="bg-[#0a1628] border border-[#d4a84b]/30 rounded-2xl p-6 max-w-md w-full"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-lg font-bold text-[#d4a84b]">Install on iPhone / iPad</h3>
              <button onClick={() => setShowIosHelp(false)} className="text-[#71717a] hover:text-white">
                <X className="w-5 h-5" />
              </button>
            </div>
            <div className="space-y-3 text-sm text-[#a1a1aa]">
              <div className="flex gap-3">
                <span className="bg-[#d4a84b]/20 text-[#d4a84b] font-bold w-6 h-6 rounded-full flex items-center justify-center shrink-0 text-xs">1</span>
                <p>Open this app in <strong className="text-white">Safari</strong> (not Chrome). Chrome on iOS cannot install PWAs.</p>
              </div>
              <div className="flex gap-3">
                <span className="bg-[#d4a84b]/20 text-[#d4a84b] font-bold w-6 h-6 rounded-full flex items-center justify-center shrink-0 text-xs">2</span>
                <p>Tap the <strong className="text-white">Share</strong> button at the bottom of Safari (the square with an arrow pointing up).</p>
              </div>
              <div className="flex gap-3">
                <span className="bg-[#d4a84b]/20 text-[#d4a84b] font-bold w-6 h-6 rounded-full flex items-center justify-center shrink-0 text-xs">3</span>
                <p>Scroll down and tap <strong className="text-white">"Add to Home Screen"</strong>.</p>
              </div>
              <div className="flex gap-3">
                <span className="bg-[#d4a84b]/20 text-[#d4a84b] font-bold w-6 h-6 rounded-full flex items-center justify-center shrink-0 text-xs">4</span>
                <p>Tap <strong className="text-white">"Add"</strong> in the top-right corner. The Wolffs InstaAlerts icon will appear on your Home Screen.</p>
              </div>
              <div className="bg-[#d4a84b]/10 border border-[#d4a84b]/20 rounded-lg p-3 mt-4">
                <p className="text-xs text-[#d4a84b]"><strong>Note:</strong> iOS does not support push notifications inside Safari. You'll receive alerts only while the app is open. Keep the app open for live signals.</p>
              </div>
            </div>
            <Button
              onClick={() => setShowIosHelp(false)}
              className="w-full mt-4 bg-[#d4a84b] text-[#0a1628] hover:bg-[#c49a40]"
              data-testid="ios-help-close"
            >
              Got it
            </Button>
          </div>
        </div>
      )}
    </div>
  );
};

// Main App Component
function App() {
  const [authView, setAuthView] = useState('login');

  return (
    <AuthProvider>
      <AppContent authView={authView} setAuthView={setAuthView} />
    </AuthProvider>
  );
}

const AppContent = ({ authView, setAuthView }) => {
  const { user, loading, refreshUser } = useAuth();

  // Listen for service worker updates
  useEffect(() => {
    if ('serviceWorker' in navigator) {
      navigator.serviceWorker.addEventListener('message', (event) => {
        if (event.data && event.data.type === 'SW_UPDATED') {
          toast.success('App updated! Refresh to see changes.', {
            duration: 10000,
            action: {
              label: 'Refresh',
              onClick: () => window.location.reload()
            }
          });
        }
      });
      
      // Check for updates periodically
      navigator.serviceWorker.ready.then((registration) => {
        registration.update();
      });
    }
  }, []);

  if (loading) {
    return (
      <div className="auth-page">
        <div className="text-center">
          <img src="/logo.png" alt="Logo" className="w-20 h-20 mx-auto mb-4 animate-pulse" />
          <p className="text-[#71717a]">Loading...</p>
        </div>
      </div>
    );
  }

  // Not logged in
  if (!user) {
    return authView === 'login' 
      ? <LoginPage onSwitchToRegister={() => setAuthView('register')} />
      : <RegisterPage onSwitchToLogin={() => setAuthView('login')} />;
  }

  // Admin user
  if (user.role === 'admin') {
    return <AdminDashboard />;
  }

  // User needs to select plan
  if (user.membership_status === 'pending' || user.membership_status === 'pending_payment' || user.membership_status === 'expired') {
    return <PlanSelectionPage />;
  }

  // Verified user - check if strategies are selected
  if (user.membership_status === 'trial' || user.membership_status === 'paid') {
    const hasStrategies = user.selected_strategies && user.selected_strategies.length > 0;
    if (!hasStrategies) {
      return <StrategySelectionPage onDone={refreshUser} />;
    }
    return <AlertDashboard />;
  }

  // Fallback to plan selection
  return <PlanSelectionPage />;
};

export default App;
